<template>
    <div>
        <!-- HEADER -->
        <div class="cal-header">
            <!-- GROUP BY TABS -->
            <div class="tabs-row">
                <tabs-group :tabs="groupByTabs" :value="currentGroupBy" @change="currentGroupBy = $event" />
            </div>

            <!-- FILTERS ROW -->
            <div class="filters-row">
                <div class="filters-left">
                    <!-- MONTH SELECTOR -->
                    <month-selector v-if="$refs.calendar" @prev="$refs.calendar.prev()" @next="$refs.calendar.next()"
                        :disabled="loadingEvents" :title="$refs.calendar.title" :date="currentCalendarDate"
                        @month="onSelectMonthCalendar" />
                    <!-- STUDENT FILTER -->
                    <kimsa-combobox v-if="currentGroupBy === 'students'" :placeholder="$t('inputs.students_placeholder')"
                        dense solo :items="students" @change="setCurrentStudent" :value="currentStudent"
                        item-value="objectID" clearable searchable with-list-icon icon="$account" class="header-select" />
                    <!-- GROUP FILTER -->
                    <kimsa-combobox v-if="currentGroupBy === 'groups'" :placeholder="$t('inputs.groups_placeholder')"
                        dense solo :items="groupsWithIcons" @change="setCurrentGroup" :value="currentGroup"
                        item-value="objectID" clearable searchable with-list-icon icon="$account_multiple"
                        class="header-select" />
                    <!-- TEAM FILTER -->
                    <template v-if="currentGroupBy === 'teams'">
                        <kimsa-combobox :placeholder="$t('inputs.teams_placeholder')" dense solo :items="teams"
                            @change="setCurrentTeam" :value="currentTeam" @clear="resetTeam" item-value="objectID"
                            clearable searchable icon="$account_multiple" class="header-select" />
                        <kimsa-combobox :placeholder="$t('inputs.sub_teams_placeholder')" dense solo
                            :items="teamGroups" @change="setCurrentTeamGroup" :readonly="!currentTeam"
                            :value="currentTeamGroup" item-value="objectID" clearable searchable with-list-icon
                            icon="$account_multiple" class="header-select" />
                    </template>
                </div>

                <div class="filters-right">
                    <template v-if="isSelectedOption">
                        <history-controls context="calendar" :height="34" />
                        <span class="cal-history-sep" />
                    </template>
                    <button v-for="opt in dateTypesOptions" :key="opt.value" class="view-btn"
                        :class="{ active: dateType === opt.value && !progressView }" @click="onChangeDateType(opt.value)">
                        {{ opt.name }}
                        <v-icon v-if="opt.value === 'day' && !can('dayView')" size="12">mdi-lock-outline</v-icon>
                    </button>
                </div>
            </div>

            <!-- WORK TYPE ROW -->
            <div class="worktype-row" v-if="currentSelection && !isDayView">
                <div class="worktype-left">
                    <span class="worktype-label">{{ $t('common.work_by') }}</span>
                    <kimsa-combobox dense solo :items="weekOptions" @change="onChangeWeekOption"
                        :value="currentWeekOption" class="header-select worktype-select" />
                    <kimsa-radio-group v-model="currentWeekStatus" :items="weekStatus" />
                </div>
                <div class="worktype-right">
                    <button type="button" class="view-btn view-btn--icon" :class="{ active: progressView }"
                        @click="progressView = true">
                        <v-icon size="15">mdi-chart-timeline-variant</v-icon>
                        {{ $t('progression.title') }}
                    </button>
                    <button type="button" class="view-btn view-btn--icon" :class="{ 'view-btn--on': libraryOpen }"
                        @click="toggleLibrary">
                        <v-icon size="15">mdi-view-grid-outline</v-icon>
                        {{ $t('common.library') }}
                    </button>
                </div>
            </div>
        </div>
        <!-- BODY -->
        <div>
            <!-- ALERT -->
            <div v-if="!isSelectedOption" class="cal-empty">
                <v-icon size="40" color="txtMuted">mdi-calendar-blank-outline</v-icon>
                <span class="cal-empty-text">{{ alertMessage }}</span>
            </div>
            <!-- LOADER -->
            <div v-else-if="loadingEvents" class="cal-loader">
                <skeleton-loader :type="skeletonType" />
            </div>
            <!-- CALENDAR -->
            <div v-show="isSelectedOption && !loadingEvents" ref="calBody"
                :class="['cal-body', { 'is-day': isDayView, 'is-dragging': dragging, 'with-library': libraryOpen }]" :style="weekHeightStyle">
                <div class="p-relative">
                    <v-calendar v-show="!isDayView && !progressView" ref="calendar" v-model="focus" color="primary" :events="routineEvents"
                        :event-height="26" event-color="transparent" :type="dateType" @change="updateRange" :locale="datePickerLocale"
                        :weekdays="weekdays" :weekday-format="getDayString"
                        :class="['calendar', { 'is-week': dateType === 'week', 'is-day': dateType === 'day' }]"
                        @mousedown:event="startDrag" @mouseup:event="endDrag"
                        @mouseenter:event="onHoverEvent" @mouseleave:event="onLeaveEvent"
                        @mouseenter:day="onEnterDay" @mouseleave:day="onLeaveDay"
                        @mouseup:day="endDragFreeSpace" :event-more="isDayView">
                        <!-- FOR EACH EVENT DATA -->
                        <template v-slot:event="item">
                            <div v-if="dateType !== 'day'" :data-uid="item.event.uid"
                                :data-block="`${item.event.assignedRoutineId}|${item.event.block}`"
                                :class="{ 'event-block-start': blockDividerUids.has(item.event.uid), 'event-has-handle': canDragBlock(item.event) }">
                                <div v-if="canDragBlock(item.event)" class="block-handle"
                                    :style="{ '--block-accent': item.event.color }">
                                    <v-icon size="15" class="block-handle-icon">mdi-drag-horizontal-variant</v-icon>
                                    <span class="block-handle-accent" />
                                    <span class="block-handle-label">{{ blockHandleLabel(item.event) }}</span>
                                </div>
                                <competence-event v-if="item.event.type === 'competence'" :event="item"
                                    :dragging="dragging" :class="getEventClass(item)"
                                    @click:show="showCompetence(item)" @click:delete="showDeleteCompetence(item)" />
                                <exercise-event v-else :class="getEventClass(item)" :dragging="dragging" :event="item"
                                    :changes="changesForEvent(item.event)"
                                    @click="showEvent(item)" @click:show="showEvent(item)"
                                    @click:delete="showDeleteEvent(item)" @click:set:add="onAddSet(item)"
                                    @click:set:remove="onRemoveSet(item)" @click:reps="onAdjustReps(item, $event)"
                                    @click:video="showStudentVideo" />
                            </div>
                        </template>

                        <!-- FOR MONTH VIEW -->
                        <template v-slot:day-label="{ day, month, date }">
                            <day-label-calendar :day="day" :is-current-month="thisMonth(month)"
                                :has-events-today="hasEventsToday(date)" :note="noteToday(date)"
                                :routine="routineToday(date)" :is-competence-day="isCompetenceDay(date)"
                                :can-lock-day="currentGroupBy === 'students'" :dragging="dragging" :date="date"
                                :is-student-view="currentGroupBy === 'students'" :match-day="matchDayFor(date)"
                                @click:day="viewDay(date)" @drag:day="onDayDrag(date, $event)"
                                @click:add="showAddExercise(date)" @click:copy="showCopyRoutine(date)"
                                @click:copy:week="showCopyWeek(date)" @click:lock="showLockDay([date])"
                                @click:save:program="showSaveProgramDialog(date)"
                                @click:save:routine="showSaveRoutineDialog(date)"
                                @click:save:block="showSaveBlockDialog(date)" @click:delete="showDeleteRoutine(date)"
                                @click:note="showNoteDialog(date)" @click:add:competence="showAddCompetenceDialog(date)"
                                @click:save:name="showChangeRoutineName(date)"
                                @click:assign:questionnaire="showAssignQuestionaire(date)" />
                        </template>
                        <template v-slot:day="{ month, date }">
                            <div v-if="thisMonth(month)" class="day-slot-content" :data-date="date">
                                <!-- ADD EXERCISE -->
                                <div class="text-right pr-1" v-if="hasEventsToday(date) && !isCompetenceDay(date)">
                                    <button-tooltip @click="showAddBlock(date)" fab text :disabled="dragging"
                                        height="30" width="30" :tooltip="$t('tooltips.add_block')">
                                        <v-icon size="15" color="txtMuted">mdi-code-brackets</v-icon>
                                    </button-tooltip>
                                    <button-tooltip @click="showAddExercise(date)" fab text :disabled="dragging"
                                        height="30" width="30" :tooltip="$t('tooltips.add_exercise')">
                                        <v-icon size="15" color="txtMuted">$add_circle</v-icon>
                                    </button-tooltip>
                                </div>
                                <!-- NO ROUTINE TODAY -->
                                <no-routines-in-date v-else-if="!isCompetenceDay(date)" :dragging="dragging" :can-drop="canDropOnEmptyDay"
                                    @click:add_block="showAddBlock(date, true)" />
                            </div>
                        </template>

                        <!-- FOR DAY/WEEK VIEW -->
                        <template v-slot:day-label-header="{ day, month, date }">
                            <day-label-calendar :day="day" :is-current-month="thisMonth(month)"
                                :has-events-today="hasEventsToday(date)" :note="noteToday(date)"
                                :routine="routineToday(date)" :is-competence-day="isCompetenceDay(date)"
                                :can-lock-day="currentGroupBy === 'students'" :dragging="dragging" :date="date"
                                :is-student-view="currentGroupBy === 'students'"
                                @click:day="viewDay(date)"
                                @click:add="showAddExercise(date)" @click:copy="showCopyRoutine(date)"
                                @click:copy:week="showCopyWeek(date)" @click:lock="showLockDay(date)"
                                @click:save:program="showSaveProgramDialog(date)"
                                @click:save:routine="showSaveRoutineDialog(date)"
                                @click:save:block="showSaveBlockDialog(date)" @click:delete="showDeleteRoutine(date)"
                                @click:note="showNoteDialog(date)" @click:add:competence="showAddCompetenceDialog(date)"
                                :is-day-view="isDayView" @click:save:content="onSaveContent(date)"
                                :saving-content="savingContent" @click:save:name="showChangeRoutineName(date)"
                                @click:assign:questionnaire="showAssignQuestionaire(date)" />
                        </template>
                        <template v-slot:day-header="{ month, date }">
                            <div v-if="thisMonth(month)" :data-date="date">
                                <template v-if="!isDayView">
                                    <!-- ADD EXERCISE -->
                                    <div class="text-right pr-1" v-if="hasEventsToday(date) && !isCompetenceDay(date)">
                                        <button-tooltip @click="showAddBlock(date)" fab text :disabled="dragging"
                                            height="30" width="30" :tooltip="$t('tooltips.add_block')">
                                            <v-icon size="15" color="txtMuted">mdi-code-brackets</v-icon>
                                        </button-tooltip>
                                        <button-tooltip @click="showAddExercise(date)" fab text :disabled="dragging"
                                            height="30" width="30" :tooltip="$t('tooltips.add_exercise')">
                                            <v-icon size="15" color="txtMuted">$add_circle</v-icon>
                                        </button-tooltip>
                                    </div>
                                    <!-- NO ROUTINE TODAY -->
                                    <no-routines-in-date v-else-if="!isCompetenceDay(date)" :dragging="dragging" :can-drop="canDropOnEmptyDay"
                                        @click:add_block="showAddBlock(date, true)" />
                                </template>
                            </div>
                        </template>
                    </v-calendar>

                    <!-- PROGRESSION -->
                    <calendar-progression v-if="progressView && !isDayView" :scope="progressionScope" :coach-id="coachId"
                        :routine-number="routineNumber" :revision="calendarRevision" :track="trackCalendar"
                        :persist="persistRoutineBlocks" />

                    <!-- DAY EDIT CONTENT -->
                    <day-calendar-content v-if="focus && isDayView" :date="focus" :current-selection="currentSelection"
                        :current-routines="currentRoutines" :collection="collectionName" :coach-id="coachId"
                        :routine-number="routineNumber" ref="dayCalendarContent" @saving="savingContent = $event"
                        :track-change="trackCalendar"
                        :assigned-routine-to-replace="assignedRoutineToReplace" :note="noteToday(focus)"
                        :can-lock-day="currentGroupBy === 'students'" :is-student-view="currentGroupBy === 'students'"
                        @click:note="showNoteDialog(focus)" @click:copy="showCopyRoutine(focus)"
                        @click:copy:week="showCopyWeek(focus)" @click:save:routine="showSaveRoutineDialog(focus)"
                        @click:save:program="showSaveProgramDialog(focus)" @click:save:block="showSaveBlockDialog(focus)"
                        @click:save:name="showChangeRoutineName(focus)" @click:delete="showDeleteRoutine(focus)"
                        @click:lock="showLockDay([focus])" @click:add:competence="showAddCompetenceDialog(focus)"
                        @click:assign:questionnaire="showAssignQuestionaire(focus)"
                        @use:questionnaire="useQuestionnaireForDay" />

                    <!-- OVERLAY  -->
                    <v-overlay :value="loadingDragging" class="calendar-overlay" opacity="0.3" z-index="7">
                        <v-progress-circular indeterminate color="primary" />
                    </v-overlay>

                    <!-- DRAG GHOST -->
                    <div v-if="dragging && draggingEvent && !loadingDragging" class="drag-ghost" ref="dragGhost">
                        <competence-event v-if="draggingEvent.type === 'competence'" :event="{ event: draggingEvent }" />
                        <exercise-event v-else :event="{ event: draggingEvent }"
                            :ghost-circuit-count="draggingCircuitCount" :ghost-label="draggingGhostLabel" />
                    </div>
                    <div v-if="libraryDrag || dayDrag" class="drag-ghost lib-ghost" ref="libraryGhost">
                        {{ libraryDrag ? libraryDrag.item.name : dayDrag.name }}
                    </div>
                </div>

                <!-- LOCK DAYS -->
                <lock-days-dialog :student="currentStudent" :current-lock-days="lockDays" ref="lockDaysDialog" />

                <!-- STUDENT VIDEO -->
                <watch-video-dialog :video="studentVideo" ref="studentVideoDialog" />

                <!-- ADD EXERCISE -->
                <add-exercise-desktop-dialog @success="onDialogSaved" :current-selection="currentSelection"
                    :current-routines="currentRoutines" :collection="collectionName"
                    :assigned-routine-to-replace="assignedRoutineToReplace" :coach-id="coachId" ref="addExerciseDialog"
                    :routine-number="routineNumber" />

                <!-- EDIT CIRCUIT -->
                <create-circuit-dialog ref="createCircuitDialog" :collection="collectionName" :coachId="coachId"
                    @success="onDialogSaved" calendar-mode />

                <!-- ADD BLOCK -->
                <add-block-dialog @success="onDialogSaved" :current-selection="currentSelection"
                    :current-routines="currentRoutines" :collection="collectionName"
                    :assigned-routine-to-replace="assignedRoutineToReplace" :coach-id="coachId" ref="addBlockDialog"
                    :routine-number="routineNumber" />

                <!-- SAVE ROUTINE -->
                <save-assigned-routine-dialog :coach-id="coachId" :collection="collectionName" ref="saveRoutine" />

                <!-- SAVE PROGRAM -->
                <save-program-dialog :coach-id="coachId" :current-selection="currentSelection" ref="saveProgram"
                    :routine-number="routineNumber" @success="onProgramCreated" />

                <!-- SAVE BLOCK -->
                <save-block-dialog :coach-id="coachId" ref="saveBlock" />

                <!-- COPY ROUTINE -->
                <copy-routine-dialog :coach-id="coachId" :dates="dates" @success="onCopyRoutine" :before-copy="onBeforeCopy"
                    :students-options="students" :groups-options="groups" :teams-options="teams"
                    :current-selection="currentSelection" ref="copy" :routine-number="routineNumber" />

                <!-- SELECT REPLACE ROUTINES -->
                <select-replace-assigned-routines-dialog :assigned-routines-in-same-date="assignedRoutinesInSameDate"
                    @confirm="onConfirmReplace($event)" ref="replaceRoutines" />

                <!-- CONFIRM DELETE -->
                <kimsa-confirm :title="confirmTitle" :content="confirmContent"
                    @confirm="onConfirmDelete" :close-on-confirm="false"
                    :loading="loadingDelete" ref="confirm" :alert-mode="true" />

                <!-- CONFIRM COPY WEEK -->
                <kimsa-confirm :title="$t('routines.copy_week_title')" :content="$t('routines.copy_week_subtitle')"
                    @confirm="onConfirmCopyWeek()" :close-on-confirm="false" :loading="loadingCopyWeek"
                    ref="copyWeek" />

                <!-- ADD COMPETENCE -->
                <add-competence-dialog :current-selection="currentSelection" :collection="collectionName"
                    :coach-id="coachId" :assigned-routine-to-replace="assignedRoutineToReplace" ref="competence"
                    @saved="onCompetenceDialogSaved" :routine-number="routineNumber" />

                <!-- ASSIGN QUESTIONNAIRE -->
                <assign-questionnaire-dialog :collection="collectionName" :coach-id="coachId"
                    :assigned-routine-to-replace="assignedRoutineToReplace" ref="questionnaire"
                    @assigned="onAssignQuestionnaire" :routine-number="routineNumber" />

                <!-- CONFIRM DELETE -->
                <kimsa-confirm :title="$t('common.delete_competence')" :content="$t('common.delete_competence_message')"
                    @confirm="onDeleteCompetence" :close-on-confirm="false" :loading="loadingDelete"
                    ref="deleteCompetence" :alert-mode="true" />

                <!-- ADD NOTES TO ROUTINE -->
                <notes-routine-dialog :current-selection="currentSelection" :collection="collectionName" ref="notes"
                    @saved="onSaveNotes" :routine-number="routineNumber" />

                <!--CHANGE ROUTINE NAME -->
                <change-name-dialog ref="changeRoutineName" @save="onSaveRoutineName" />

                <!-- DELETE WITH DRAGGING -->
                <delete-dragging-button :dragging="dragging" @mouseup="onDropOnDelete"
                    :bottom="76" :right="libraryOpen ? 372 : 16" :size="48" />

                <!-- LIBRARY -->
                <calendar-library-drawer v-model="libraryOpen" :coach-id="coachId" :top="libraryTop"
                    :drop-active="libraryDropActive" :drop-label="libraryDropLabel" :new-ids="libraryNewIds"
                    @drag-start="onLibraryDragStart" @item-click="onLibraryItemClick" ref="library" />
                <apply-program-dialog ref="applyProgram" :visible-dates="applyVisibleDates" :loading="applyingProgram"
                    @apply="onApplyProgram" />
                <save-to-library-dialog ref="saveToLibrary" :coach-id="coachId" @saved="onLibrarySaved" />

                <note-reminder-dialog />
            </div>
        </div>
        <!-- BLOCKS FOOTER -->
        <block-footer v-show="!isDayView && !progressView" :colors="blockColors" />
        <div v-if="blockColors.length > 0" class="switch-routine-wrapper" :style="libraryOpen ? { right: '364px' } : null">
            <button-tooltip left :bottom="false"
                :tooltip="$t('tooltips.switch_routines', { routineNumber: routineNumber === 1 ? 2 : 1 })">
                <template v-slot:activator="{ on, attrs }">
                    <button class="switch-routine-fab" v-bind="attrs" v-on="on" @click="switchRoutine">
                        <span class="switch-routine-badge">{{ routineNumber }}</span>
                        <v-icon size="20">mdi-swap-horizontal</v-icon>
                    </button>
                </template>
            </button-tooltip>
        </div>
    </div>
</template>

<script>

import draggable from 'vuedraggable'
import AddExerciseDesktopDialog from "@/components/coach/routines/AddExerciseDesktopDialog";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { UserIndex, PrescriptionIndex } from "@/utils/Algolia";
import { mapActions, mapState, mapMutations } from "vuex";
import CopyRoutineDialog from "@/components/common/routines/CopyRoutineDialog";
import { randomString } from '@/utils/Common'
import cloneDeep from 'lodash/cloneDeep'
import {
    addAssignedRoutinesGroup, clearBlocksAssignedRoutine,
    deleteAssignedRoutinesGroup,
    deleteAssignedRoutineStudentInDate,
    getMergedBlocks, getPayloadBaseAssignedRoutine, getPayloadExercise, exerciseKey,
    updateAssignedRoutinesGroup, getPayloadBaseAssignedCompetence, addAssignedCompetenceGroup,
    assignRoutineToSelection, assignProgramDates, defaultProgramWeekdays, programSessionDates, exerciseSetsLabel
} from "@/utils/Exercises";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { db, getAllByIds, FieldValue } from "@/utils/Firebase";
import SelectReplaceAssignedRoutinesDialog from "@/components/coach/routines/SelectReplaceAssignedRoutinesDialog";
import SaveAssignedRoutineDialog from "@/components/coach/routines/SaveAssignedRoutineDialog";
import SaveProgramDialog from "@/components/coach/routines/SaveProgramDialog";
import BlockFooter from "@/components/coach/calendar/BlockFooter";
import TabsGroup from "@/components/common/TabsGroup";
import MonthSelector from "@/components/coach/calendar/MonthSelector";
import KimsaRadioGroup from "@/components/common/inputs/KimsaRadioGroup";
import { BLOCK_COLORS } from "@/utils/BlockColors";

import SkeletonLoader from "@/components/common/SkeletonLoader";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import ExerciseEvent from "@/components/calendar/ExerciseEvent";
import NoRoutinesInDate from "@/components/calendar/NoRoutinesInDate";
import DayLabelCalendar from "@/components/calendar/DayLabelCalendar";
import LockDaysDialog from "@/components/calendar/LockDaysDialog";
import NotesRoutineDialog from "@/components/coach/NotesRoutineDialog";
import SaveBlockDialog from '@/components/coach/routines/SaveBlockDialog';
import AddBlockDialog from '@/components/coach/routines/AddBlockDialog';
import AddCompetenceDialog from '../competences/AddCompetenceDialog.vue';
import CompetenceEvent from '../../calendar/CompetenceEvent.vue';
import { getBlockNextPrescription, uniqueDayRoutine } from '../../../utils/Exercises';
import { matchDayLabel } from '@/utils/MatchDay';
import { getStudentChanges, studentChangeKey, pruneRoutineChanges } from '@/utils/Routine';
import WatchVideoDialog from '@/components/common/routines/WatchVideoDialog';
import DayCalendarContent from './DayCalendarContent.vue';
import ChangeNameDialog from './ChangeNameDialog.vue';
import DeleteDraggingButton from '../../common/DeleteDraggingButton.vue';
import AssignQuestionnaireDialog from './AssignQuestionnaireDialog.vue';
import NoteReminderDialog from '../NoteReminderDialog.vue';
import CreateCircuitDialog from './CreateCircuitDialog.vue';
import HistoryControls from '@/components/common/HistoryControls';
import calendarHistoryMixin from '@/mixins/calendarHistoryMixin';
import CalendarLibraryDrawer from '@/components/coach/calendar/CalendarLibraryDrawer';
import ApplyProgramDialog from '@/components/coach/calendar/ApplyProgramDialog';
import SaveToLibraryDialog from '@/components/coach/calendar/SaveToLibraryDialog';
import CalendarProgression from '@/components/coach/calendar/CalendarProgression';
import { pushHistory, registerHistoryFlusher } from '@/utils/History';
import { CALENDAR_HISTORY } from '@/utils/CalendarHistory';

const WEEK_BOTTOM_SPACE = 72

export default {
    name: "CalendarDesktop",
    components: {
        NotesRoutineDialog,
        LockDaysDialog,
        DayLabelCalendar,
        NoRoutinesInDate,
        ExerciseEvent,
        ButtonTooltip,
        SkeletonLoader,
        MonthSelector,
        TabsGroup,
        KimsaRadioGroup,
        BlockFooter,
        SaveProgramDialog,
        SaveAssignedRoutineDialog,
        SelectReplaceAssignedRoutinesDialog,
        KimsaConfirm,
        CopyRoutineDialog,
        KimsaCombobox,
        AddExerciseDesktopDialog,
        draggable,
        SaveBlockDialog,
        AddBlockDialog,
        AddCompetenceDialog,
        CompetenceEvent,
        DayCalendarContent,
        ChangeNameDialog,
        DeleteDraggingButton,
        AssignQuestionnaireDialog,
        NoteReminderDialog,
        CreateCircuitDialog,
        WatchVideoDialog,
        HistoryControls,
        CalendarLibraryDrawer,
        ApplyProgramDialog,
        SaveToLibraryDialog,
        CalendarProgression,
    },
    mixins: [calendarHistoryMixin],
    data: (vm) => ({
        // Progression
        progressView: false,
        calendarRevision: 0,
        // Library
        libraryOpen: false,
        libraryTop: 0,
        libraryDrag: null,
        dayDrag: null,
        libraryDropActive: false,
        libraryDropLabel: '',
        libraryNewIds: [],
        applyingProgram: false,
        applyVisibleDates: [],

        // Calendar
        studentVideo: null,
        firstTime: true,
        focus: '',
        dateTypesOptions: [
            { name: vm.$t('common.day'), value: 'day' },
            { name: vm.$t('common.week'), value: 'week' },
            { name: vm.$t('common.month'), value: 'month' }
        ],
        groupByTabs: [
            { label: vm.$tc('student.student', 2), value: 'students' },
            { label: `${vm.$t('common.group')}s`, value: 'groups' },
            { label: `${vm.$t('common.team')}s`, value: 'teams' },
        ],
        dateType: 'month',
        weekHeadHeight: null,
        weekdays: [1, 2, 3, 4, 5, 6, 0],
        currentMonth: null,
        currentYear: null,
        days: [
            vm.$t('days.sunday'),
            vm.$t('days.monday'),
            vm.$t('days.tuesday'),
            vm.$t('days.wednesday'),
            vm.$t('days.thursday'),
            vm.$t('days.friday'),
            vm.$t('days.saturday')
        ],

        // Delete confirm
        loadingDelete: false,
        confirmTitle: '',
        confirmContent: '',
        confirmDeleteExercise: false,
        confirmDeleteBlock: false,

        // Events
        loadingEvents: false,
        highestBlock: 0,
        blockColorsBase: BLOCK_COLORS,
        blockDragMode: false,
        blockColors: [],
        events: [],
        currentEvent: null,

        // Coaches/Students
        students: [],
        currentStudent: null,
        currentGroupBy: 'students',
        currentGroup: null,
        currentTeam: null,
        currentTeamGroup: null,

        // Copy/Replace Routines
        assignedRoutinesInSameDate: [],
        assignedRoutineToReplace: [],
        currentDate: null,
        loadingCopyWeek: false,

        // Week/Month progress
        weekStatus: [
            { name: vm.$t('common.finished'), value: 'finished', color: '#4cb351' },
            { name: vm.$t('common.incomplete'), value: 'incomplete', color: '#ac7d1d' },
            { name: vm.$t('common.to_be_done'), value: 'to_be_done', color: '#9a2d2d' },
        ],
        currentWeekStatus: null,
        weekOptions: [
            { name: vm.$t('common.week'), value: 'week' },
            { name: vm.$t('common.month'), value: 'month' },
        ],
        currentWeekOption: 'week',

        // LockDays
        lockDays: [],

        // Dragging
        dragging: false,
        draggingEvent: null,
        startX: 0,
        startY: 0,
        loadingDragging: false,
        isCompetence: false,
        animationFrame: null,

        lastClickTime: 0,
        clickTimeout: null,
        doubleClickDelay: 250,

        routineNumber: 1,
        savingContent: false,
    }),
    computed: {
        ...mapState(['routinesStudent', 'groupRoutines', 'teamGroupRoutines', 'routineNotes', 'groups', 'teams', 'libraryCoach']),
        dates(vm = this) {
            const uniqueDates = new Set();
            const dates = [];

            vm.events
                .forEach(event => {
                    const uniqueKey = `${event.date}`;
                    if (!uniqueDates.has(uniqueKey)) {
                        uniqueDates.add(uniqueKey);
                        dates.push({
                            date: vm.$moment(event.date).format('DD-MM-YYYY'),
                            routineId: event.routineId,
                            assignedRoutineId: event.assignedRoutineId,
                            routineNumber: event.routineNumber,
                        });
                    }
                });

            return dates.sort((a, b) => Number(a.date.substr(0, 2)) - Number(b.date.substr(0, 2)));
        },
        teamGroups(vm = this) {
            return vm.currentTeam?.groups.map(group => ({
                teamId: vm.currentTeam.id,
                ...group,
                id: group.id,
                leftIcon: 'mdi-account-multiple',
                rightIcon: vm.getIconWeek(group.weekStatus || 'to_be_done')
            })) || []
        },
        isSelectedOption(vm = this) {
            return vm.currentStudent || vm.currentGroup || vm.currentTeamGroup
        },
        alertMessage(vm = this) {
            if (vm.currentGroupBy === 'students') return vm.$t('exercises.need_select_an_student')
            if (vm.currentGroupBy === 'groups') return vm.$t('exercises.need_select_an_group')
            if (vm.currentGroupBy === 'teams') return vm.$t('exercises.need_select_an_team')
            return ''
        },
        currentRoutines(vm = this) {
            if (vm.currentGroupBy === 'students') return vm.routinesStudent
            if (vm.currentGroupBy === 'groups') return vm.groupRoutines
            if (vm.currentGroupBy === 'teams') return vm.teamGroupRoutines
            return []
        },
        currentSelection(vm = this) {
            if (vm.currentGroupBy === 'students') return vm.currentStudent
            if (vm.currentGroupBy === 'groups') return vm.currentGroup
            if (vm.currentGroupBy === 'teams') return vm.currentTeamGroup
            return null
        },
        collectionName(vm = this) {
            return vm.currentGroupBy === 'students' ? 'assignedRoutines' : 'groupAssignedRoutines'
        },
        groupsWithIcons(vm = this) {
            return vm.groups.map(group => ({
                ...group,
                id: group.id,
                leftIcon: 'mdi-account-multiple',
                rightIcon: vm.getIconWeek(group.weekStatus || 'to_be_done')
            }))
        },
        coachId(vm = this) {
            return vm.isCompany ? vm.libraryCoach?.id || null : vm.currentUser?.uid || null
        },
        routineEvents(vm = this) {
            let routineEvents = vm.events.filter(event => event.start && event.routineNumber === vm.routineNumber)
            return vm.isDayView ? [] : routineEvents;
        },
        progressionScope(vm = this) {
            return vm.calendarHistoryScope()
        },
        isDayView(vm = this) {
            return vm.dateType === 'day'
        },
        skeletonType(vm = this) {
            if (vm.dateType === 'day') return 'calendar-day'
            return vm.dateType === 'week' ? 'calendar-week' : 'calendar'
        },
        currentCalendarDate(vm = this) {
            return vm.$refs?.calendar?.lastStart?.date || null
        },
        eventsByDate(vm = this) {
            const map = new Map()
            for (const e of vm.events) {
                if (e.routineNumber !== vm.routineNumber) continue
                if (!map.has(e.date)) map.set(e.date, [])
                map.get(e.date).push(e)
            }
            return map
        },
        blockBoundaries(vm = this) {
            const starts = new Set()
            const dividers = new Set()
            for (const evs of vm.eventsByDate.values()) {
                const sorted = evs.filter(e => e.type !== 'competence').sort((a, b) => a.block - b.block)
                let prevBlock = null
                sorted.forEach(e => {
                    if (e.block !== prevBlock) {
                        starts.add(e.uid)
                        if (prevBlock !== null) dividers.add(e.uid)
                    }
                    prevBlock = e.block
                })
            }
            return { starts, dividers }
        },
        blockStartUids(vm = this) {
            return vm.blockBoundaries.starts
        },
        blockDividerUids(vm = this) {
            return vm.blockBoundaries.dividers
        },
        competenceByDate(vm = this) {
            const map = new Map()
            for (const e of vm.events) {
                if (e.routineNumber !== vm.routineNumber) continue
                if (e.type !== 'competence') continue
                map.set(e.date, true)
            }
            return map
        },
        competenceDates(vm = this) {
            return [...vm.competenceByDate.keys()].sort()
        },
        routinesByDate(vm = this) {
            const map = new Map()
            for (const r of vm.currentRoutines) {
                if (r.routineNumber !== vm.routineNumber) continue
                const date = r.rawDate?.full
                if (!date) continue
                map.set(date, r)
            }
            return map
        },
        studentChanges(vm = this) {
            const map = new Map()
            if (vm.currentGroupBy !== 'students') return map
            for (const routine of vm.currentRoutines) {
                const routineId = routine.assignedRoutineId || routine.id
                if (!routineId) continue
                getStudentChanges(routine).forEach((groups, key) => map.set(`${routineId}|${key}`, groups))
            }
            return map
        },
        notesByDate(vm = this) {
            const map = new Map()
            for (const n of vm.routineNotes || []) {
                if (n.routineNumber !== vm.routineNumber) continue
                const date = n.rawDate?.full
                if (!date) continue
                map.set(date, n)
            }
            return map
        },
        draggingSourceKey(vm = this) {
            if (!vm.dragging || !vm.draggingEvent) return null
            if (!vm.blockDragMode && !vm.draggingEvent.isCircuit) return null
            return `${vm.draggingEvent.assignedRoutineId}|${vm.draggingEvent.block}`
        },
        draggingCircuitCount(vm = this) {
            const d = vm.draggingEvent
            if (!d || (!vm.blockDragMode && !d.isCircuit)) return 0
            return vm.events.filter(e =>
                e.assignedRoutineId === d.assignedRoutineId &&
                e.block === d.block &&
                e.routineNumber === vm.routineNumber
            ).length
        },
        draggingGhostLabel(vm = this) {
            const d = vm.draggingEvent
            if (!vm.blockDragMode || !d) return null
            const date = d.date ? vm.$moment(d.date).format('D MMM') : ''
            return date ? `${vm.blockHandleLabel(d)} · ${date}` : vm.blockHandleLabel(d)
        },
        canDropOnEmptyDay(vm = this) {
            return vm.dragging && !!vm.draggingEvent
        },
        weekHeightStyle(vm = this) {
            if (vm.dateType !== 'week' || !vm.weekHeadHeight) return null
            return { '--week-head-h': `${vm.weekHeadHeight}px` }
        },
    },
    created() {
        const vm = this
        vm._pendingEdits = {}
        vm._unregisterFlusher = registerHistoryFlusher(CALENDAR_HISTORY, () => vm.flushPendingEdits())
    },
    async mounted() {
        const vm = this
        await vm.loadStudents()
        if (vm.$refs.calendar) vm.$refs.calendar.checkChange()

        vm.updateWeekHeight()
        window.addEventListener('resize', vm.updateWeekHeight)
        if (window.ResizeObserver) {
            vm.weekResizeObserver = new ResizeObserver(() => vm.updateWeekHeight())
            vm.weekResizeObserver.observe(document.body)
            const header = vm.$el.querySelector('.cal-header')
            if (header) vm.weekResizeObserver.observe(header)
        }
    },
    beforeDestroy() {
        if (this._unregisterFlusher) this._unregisterFlusher()
        window.removeEventListener('mousemove', this.onDragMove)
        window.removeEventListener('mouseup', this.onWindowMouseUp)
        window.removeEventListener('mousemove', this.onLibraryDragMove)
        window.removeEventListener('mouseup', this.onLibraryDragEnd)
        window.removeEventListener('mousemove', this.onDayDragMove)
        window.removeEventListener('mouseup', this.onDayDragEnd)
        window.removeEventListener('scroll', this.updateLibraryTop, true)
        window.removeEventListener('resize', this.updateLibraryTop)
        window.removeEventListener('resize', this.updateWeekHeight)
        if (this.weekResizeObserver) this.weekResizeObserver.disconnect()
    },
    methods: {
        ...mapActions(['fetchRoutinesStudent', 'fetchGroupRoutines', 'fetchTeamGroupRoutines', 'fetchRoutineNotes']),

        updateWeekHeight() {
            const vm = this
            if (vm.dateType !== 'week') return
            const el = vm.$refs.calBody
            if (!el) return
            const documentTop = el.getBoundingClientRect().top + window.scrollY
            const target = Math.max(320, Math.round(window.innerHeight - documentTop - WEEK_BOTTOM_SPACE))
            if (Math.abs((vm.weekHeadHeight || 0) - target) > 1) vm.weekHeadHeight = target
        },
        ...mapMutations(['updateAssignedQuestionnaireId']),

        // Loaders
        async loadStudents() {
            const vm = this
            if (!vm.coachId) { vm.students = []; return }
            let filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`
            let response = await UserIndex.search('', { filters: filter, hitsPerPage: 1000 })
            vm.students = response.hits.map(hit => {
                let leftIcon = (!!hit.team && hit.team) || (!!hit.group && hit.group) ? 'mdi-account-multiple' : 'mdi-account'
                if (hit.hasInjury) leftIcon = 'mdi-account-injury-outline'

                return {
                    ...hit,
                    leftIcon: leftIcon,
                    rightIcon: vm.getIconWeek(hit.weekStatus || 'to_be_done')
                }
            })
            let studentId = vm.$route.query.studentId
            if (studentId) vm.currentStudent = vm.students.find(student => student.objectID === studentId)
        },
        async loadRoutines(date, endDate) {
            const vm = this
            let startDate = vm.$moment(date, 'YYYY-M-DD').format('YYYY-MM-DD')
            if (!endDate && vm.$refs.calendar?.lastStart?.date && vm.$refs.calendar?.lastEnd?.date) {
                startDate = vm.$moment(vm.$refs.calendar.lastStart.date).format('YYYY-MM-DD')
                endDate = vm.$refs.calendar.lastEnd.date
            }
            let endDateFormatted = endDate ? vm.$moment(endDate, 'YYYY-M-DD').format('YYYY-MM-DD') : null
            if (vm.currentGroupBy === 'students' && vm.currentStudent?.objectID)
                await vm.fetchRoutinesStudent({
                    studentId: vm.currentStudent.objectID,
                    coachId: vm.coachId,
                    date: startDate,
                    endDate: endDateFormatted,
                })
            if (vm.currentGroupBy === 'groups' && vm.currentGroup?.id)
                await vm.fetchGroupRoutines({
                    groupId: vm.currentGroup.id,
                    date: startDate,
                })
            if (vm.currentGroupBy === 'teams' && vm.currentTeam?.id && vm.currentTeamGroup)
                await vm.fetchTeamGroupRoutines({
                    teamId: vm.currentTeam.id,
                    groupId: vm.currentTeamGroup.id,
                    date: startDate,
                })
        },
        async loadLockDays(date) {
            const vm = this
            if (!vm.currentStudent?.objectID) return
            let startDate = vm.$moment(date, 'YYYY-M-DD').format('YYYY-MM-DD')
            let endDate = vm.$moment(date, 'YYYY-M-DD').add(1, 'month').format('YYYY-MM-DD')

            let lockDays = await db.collection('lockDays')
                .where('studentId', '==', vm.currentStudent.objectID)
                .where('date', ">=", startDate)
                .where('date', "<", endDate)
                .orderBy('date', 'asc').get()

            vm.lockDays = lockDays.docs.map(day => {
                return { id: day.id, ...day.data() }
            })
            // MOSTRAR ESTOS LOCKDAYS EN EL CALENDARIO
        },
        async loadRoutineNotes(date) {
            const vm = this
            let startDate = vm.$moment(date, 'YYYY-M-DD').format('YYYY-MM-DD')
            await vm.fetchRoutineNotes({
                studentId: vm.currentStudent?.objectID || null,
                teamId: vm.currentTeam?.id || null,
                groupId: vm.getGroupId(),
                date: startDate,
            })
        },
        getGroupId() {
            const vm = this
            if (vm.currentGroup?.id) return vm.currentGroup.id
            if (vm.currentTeamGroup?.id || vm.currentTeamGroup?.id === 0) return vm.currentTeamGroup.id
            return null
        },

        // Calendar
        viewDay(date) {
            const vm = this
            if (!vm.requirePlan('dayView')) return
            vm.focus = date
            vm.dateType = 'day'
        },
        thisMonth(month) {
            const vm = this
            if (vm.dateType !== 'month') return true
            return vm.currentMonth === month
        },
        getDayString(date) {
            const vm = this
            return vm.days[date.weekday]
        },
        hasEventsToday(date) {
            return this.eventsByDate.has(date)
        },
        isCompetenceDay(date) {
            return this.competenceByDate.has(date)
        },
        onConfirmDelete() {
            const vm = this
            if (vm.confirmDeleteBlock) return vm.onDeleteBlock()
            if (vm.confirmDeleteExercise) return vm.onDeleteExercise()
            return vm.onDeleteRoutine()
        },
        canDragBlock(event) {
            const vm = this
            if (vm.isDayView || event.type === 'competence' || event.isCircuit) return false
            return vm.blockStartUids.has(event.uid)
        },
        blockHandleLabel(event) {
            return `${this.$tc('common.blocks', 1)} ${event.block}`
        },
        onDropOnDelete() {
            const vm = this
            if (vm.blockDragMode) return vm.showDeleteBlock({ event: vm.draggingEvent })
            vm.showDeleteEvent({ event: vm.draggingEvent })
        },
        showDeleteBlock({ event }) {
            const vm = this
            if (!event) return
            vm.dragging = false
            vm.draggingEvent = null
            vm.blockDragMode = false
            vm.currentEvent = { ...event }
            vm.confirmDeleteExercise = false
            vm.confirmDeleteBlock = true
            vm.confirmTitle = vm.$t('exercises.delete_block')
            vm.confirmContent = vm.$t('exercises.confirm_delete_block', { block: vm.blockHandleLabel(event) })
            vm.$refs.confirm.show()
        },
        async onDeleteBlock() {
            const vm = this
            await vm.withLoading(async () => {
                const current = vm.currentEvent
                await vm.trackCalendar(vm.$t('history.delete', { name: vm.blockHandleLabel(current) }), [current.date], async () => {
                    const routine = vm.currentRoutines.find(r => r.id === vm.currentEvent.assignedRoutineId)
                    if (!routine) return
                    let blocks = clearBlocksAssignedRoutine(routine.blocks, false)
                    blocks.splice(vm.currentEvent.block - 1, 1)
                    blocks = clearBlocksAssignedRoutine(blocks, false)

                    if (blocks.length === 0) await vm.deleteEmptyAssignedRoutine(vm.currentEvent.assignedRoutineId)
                    else await vm.persistRoutineBlocks(vm.currentEvent.assignedRoutineId, blocks)

                    vm.events = vm.events.filter(event => !(event.date === vm.currentEvent.date
                        && event.assignedRoutineId === vm.currentEvent.assignedRoutineId))
                    if (blocks.length > 0) {
                        const merged = await getMergedBlocks(blocks)
                        vm.pushToEvents(merged, vm.currentEvent.date, vm.currentEvent, vm.routineNumber)
                    }
                    await vm.loadRoutines(vm.currentEvent.date)
                })
                vm.$refs.confirm?.hide()
                vm.confirmDeleteBlock = false
                vm.currentEvent = null
            }, 'loadingDelete')
        },
        matchDayFor(date) {
            return matchDayLabel(date, this.competenceDates)
        },
        getAbsoluteMonths(month, year) {
            return month + (year * 12);
        },
        onChangeDateType(dateType) {
            const vm = this
            if (dateType === 'day' && !vm.requirePlan('dayView')) return
            vm.progressView = false
            vm.dateType = dateType
            if (!vm.focus) vm.focus = vm.$moment().format('YYYY-MM-DD')
        },
        triggerUpdateCalendar(currentSelection) {
            this.resetCalendarHistory()
            if (!currentSelection) return
            const vm = this
            vm.highestBlock = 0
            const start = {
                date: `${vm.currentYear}-${vm.currentMonth}-01`,
                month: vm.currentMonth,
                year: vm.currentYear,
            }

            if (vm.firstTime) {
                vm.updateRange({ start })
            } else {
                let endMonths = vm.getAbsoluteMonths(vm.$moment().month() + 1, vm.$moment().year())
                let monthDifference = endMonths - vm.getAbsoluteMonths(vm.currentMonth, vm.currentYear)
                monthDifference === 0 ? vm.updateRange({ start }) : vm.$refs.calendar.move(monthDifference)
            }
            if (vm.loadingEvents) vm.firstTime = false
        },
        async updateRange({ start, end }) {
            const vm = this;
            vm.currentMonth = start.month;
            vm.currentYear = start.year;
            if (!vm.isSelectedOption) return;
            await vm.withLoading(async () => {
            vm.events = [];

            await Promise.all([
                vm.loadRoutines(start.date, end?.date),
                vm.currentStudent ? vm.loadLockDays(start.date) : Promise.resolve(),
                vm.loadRoutineNotes(start.date),
            ]);

            vm.events = await vm.buildCalendarEvents()
            }, 'loadingEvents')
        },
        async refreshCalendarEvents() {
            const vm = this
            await vm.loadRoutines(vm.$refs.calendar?.lastStart?.date || `${vm.currentYear}-${vm.currentMonth}-01`)
            vm.events = await vm.buildCalendarEvents()
            vm.calendarRevision++
        },
        async buildCalendarEvents() {
            const vm = this
            const routines = vm.currentRoutines.slice()
            const allIds = [...new Set(
                routines
                    .flatMap(r => r.blocks || [])
                    .flatMap(b => b.exercises || [])
                    .map(e => exerciseKey(e))
                    .filter(Boolean)
            )]
            if (allIds.length) await vm.$store.dispatch('fetchExercisesCache', allIds)

            const merged = await Promise.all(
                routines.map(r => getMergedBlocks(r.blocks || []))
            )

            const allEvents = []
            merged.forEach((blocks, i) => {
                const routine = routines[i]
                if (!routine?.rawDate?.full) return
                vm.pushToEvents(
                    clearBlocksAssignedRoutine(blocks, false),
                    routine.rawDate.full,
                    routine,
                    routine.routineNumber || 1,
                    allEvents
                )
            })
            return allEvents
        },
        pushToEvents(blocks, date, routine, routineNumber, target = null) {
            const vm = this;
            const events = target || vm.events;

            if (routine.type === 'competence') {
                const event = vm.generateEvent(routine, date, 0, vm.getColorBlock(0), routine, 0, null, routineNumber);
                events.push(event);
            } else {
                blocks.forEach((block, i) => {
                    const blockColor = vm.getColorBlock(i);
                    block.exercises.forEach((exercise, j) => {
                        if (exercise.type === 'exercise') {
                            const event = vm.generateEvent(exercise, date, i, blockColor, routine, j, block, routineNumber);
                            events.push(event);
                        }
                    });
                });
            }
        },
        generateEvent(exercise, date, blockIndex, blockColor, routine, exerciseIndex, block = null, routineNumber = 1) {
            return {
                ...exercise,
                name: exercise.name || exercise.versus || this.$tc('exercises.exercises', 1),
                start: date,
                block: blockIndex + 1,
                color: blockColor,
                date: date,
                routineId: routine.routineId || null,
                assignedRoutineId: routine.assignedRoutineId || routine.id,
                assignedQuestionnaireId: routine.assignedQuestionnaireId || null,
                exerciseIndex: exerciseIndex,
                uid: exercise.uid || randomString(10, '', routine.type === 'competence' ? 'com' : 'ex'),
                type: routine.type || 'exercise',
                isCircuit: block?.isCircuit || false,
                isTabata: block?.type === 'tabata',
                sets: block?.sets || null,
                routineNumber,
                studentId: routine.studentId || null,
                notificationId: routine.notificationId || null,
                notificationHour: routine.notificationHour || null
            };
        },

        // Update calendar
        formatPayloadAddExercise(event) {
            return {
                assignedRoutineId: event.assignedRoutineId,
                routineId: event.routineId || null,
                date: event.date
            }
        },
        async updateBlockExercises(event, addEvent = false, duplicate = false, editEvent = false, dragged = null, wholeBlock = false) {
            if (!event) return []
            if (event.event) event = event.event

            const vm = this
            let currentBlock = event.block - 1
            let exerciseIndex = event.exerciseIndex

            const assignedRoutine = vm.currentRoutines.find(routine => routine.id === event.assignedRoutineId)
            if (!assignedRoutine) return []
            let arBlocks = clearBlocksAssignedRoutine(assignedRoutine.blocks, false)

            const source = addEvent ? (dragged || vm.draggingEvent) : null
            const removeFromSource = !!source && source.assignedRoutineId === event.assignedRoutineId && !duplicate

            if (!arBlocks[currentBlock] || (removeFromSource && !arBlocks[source.block - 1])) {
                await vm.onAddExercise(vm.formatPayloadAddExercise(event))
                vm.toast(vm.$t('common.error_generic'), 'error')
                return []
            }

            if (event.isCircuit || wholeBlock) {
                arBlocks.splice(currentBlock, 1)
            } else {
                if (addEvent) {
                    if (removeFromSource) {
                        arBlocks[source.block - 1].exercises.splice(source.exerciseIndex, 1)
                    }
                    const swapWithPrev = !duplicate
                        && source.assignedRoutineId === event.assignedRoutineId
                        && source.block === event.block
                        && source.exerciseIndex === exerciseIndex + 1
                    arBlocks[currentBlock].exercises.splice(swapWithPrev ? exerciseIndex : exerciseIndex + 1, 0, getPayloadExercise(source))
                } else if (editEvent) {
                    arBlocks[currentBlock].exercises.splice(exerciseIndex, 1, getPayloadExercise(event))
                } else {
                    arBlocks[currentBlock].exercises.splice(exerciseIndex, 1)
                }
            }

            arBlocks = arBlocks.filter(block => block.exercises.length > 0)

            if (arBlocks.length === 0) {
                await vm.deleteEmptyAssignedRoutine(event.assignedRoutineId)
            } else {
                const ops = [
                    db.collection(vm.collectionName).doc(event.assignedRoutineId).update({ blocks: arBlocks }),
                    pruneRoutineChanges(event.assignedRoutineId, arBlocks, vm.collectionName),
                ]
                if (vm.collectionName === 'groupAssignedRoutines') {
                    let ids = vm.currentRoutines.find(r => r.id === event.assignedRoutineId)?.assignedRoutinesIds
                    if (!ids) {
                        const routineRef = await db.collection(vm.collectionName).doc(event.assignedRoutineId).get()
                        ids = routineRef.data()?.assignedRoutinesIds || []
                    }
                    ops.push(updateAssignedRoutinesGroup(ids, arBlocks))
                }
                await Promise.all(ops)
            }
            return arBlocks
        },

        // Events Style
        getColorBlock(index) {
            const vm = this
            while (vm.blockColors.length <= index) {
                vm.blockColors.push(vm.blockColorsBase[vm.blockColors.length % vm.blockColorsBase.length]);
            }
            return vm.blockColors[index];
        },
        getEventClass(item) {
            const vm = this
            if (!vm.dragging || !vm.draggingEvent) return ''
            const ev = item.event
            if (vm.draggingSourceKey) {
                return `${ev.assignedRoutineId}|${ev.block}` === vm.draggingSourceKey ? 'drag-source drag-source-block' : ''
            }
            return ev.uid === vm.draggingEvent.uid ? 'drag-source' : ''
        },

        // Dragging
        startDrag(event, mouse) {
            const vm = this
            if (vm.loadingDragging) return
            const blockHandle = mouse.target.closest ? mouse.target.closest('.block-handle') : null
            if (!blockHandle && mouse.target.tagName !== 'DIV') {
                vm.draggingEvent = null
                vm.dragging = false
                return;
            }
            vm.startX = mouse.x;
            vm.startY = mouse.y;
            vm.draggingEvent = event.event
            vm.blockDragMode = !!blockHandle
            vm.dragging = false
            vm._hoverTargetUid = null
            vm.clearHighlight()
            window.addEventListener('mousemove', vm.onDragMove)
            window.addEventListener('mouseup', vm.onWindowMouseUp)
        },
        async endDrag(event, mouse) {
            const vm = this
            if (!vm.draggingEvent) return;
            if (vm.loadingDragging) return;

            const dragged = vm.draggingEvent
            const deltaX = Math.abs(mouse.x - vm.startX);
            const deltaY = Math.abs(mouse.y - vm.startY);
            const wasClick = deltaX < 5 && deltaY < 5;

            // COMPETENCIA arrastrada: click abre el editor, soltar sobre un dia con eventos no se permite
            if (dragged.type === 'competence') {
                if (wasClick) vm.showCompetence({ event: dragged });
                else if (event.event.assignedRoutineId !== dragged.assignedRoutineId) vm.toast(vm.$t('routines.competence_only_free_day'), 'error');
                vm.dragging = false;
                vm.draggingEvent = null;
                return;
            }

            // Soltar un ejercicio/circuito sobre una competencia no se permite
            if (event.event.type === 'competence') {
                if (!wasClick) vm.toast(vm.$t('routines.cannot_drop_on_competence'), 'error');
                vm.dragging = false;
                vm.draggingEvent = null;
                return;
            }

            if (vm.blockDragMode) {
                if (!wasClick && event.event.type !== 'competence') {
                    await vm.trackCalendar(vm.$t('history.move', { name: vm.blockHandleLabel(dragged) }), [dragged.date, event.event.date],
                        () => vm.moveCircuitBlock(dragged, event.event, true));
                }
                vm.blockDragMode = false;
                vm.dragging = false;
                vm.draggingEvent = null;
                return;
            }

            // CIRCUITO arrastrado: click abre el editor, arrastre mueve el bloque completo
            if (dragged.isCircuit === true) {
                if (wasClick) this.showEvent({ event: dragged });
                else await vm.trackCalendar(vm.$t('history.move', { name: vm.blockHandleLabel(dragged) }), [dragged.date, event.event.date],
                    () => vm.moveCircuitBlock(dragged, event.event));
                vm.dragging = false;
                vm.draggingEvent = null;
                return;
            }

            // EJERCICIO normal arrastrado a un circuito: crea un bloque nuevo en su posición
            if (event.event.isCircuit) {
                if (!wasClick) await vm.trackCalendar(vm.$t('history.move', { name: `«${dragged.name}»` }), [dragged.date, event.event.date],
                    () => vm.moveExerciseToNewBlock(dragged, event.event));
                vm.dragging = false;
                vm.draggingEvent = null;
                return;
            }

            if (wasClick) {
                const now = Date.now();
                const diff = now - this.lastClickTime;
                if (diff < this.doubleClickDelay) {
                    clearTimeout(this.clickTimeout);
                    this.lastClickTime = 0;
                    vm.dragging = false;
                    vm.draggingEvent = null;
                    this.duplicateEvent(event.event);
                    return;
                } else {
                    vm.dragging = false;
                    vm.draggingEvent = null;
                    this.lastClickTime = now;
                    this.clickTimeout = setTimeout(() => {
                        this.showEvent(event);
                    }, this.doubleClickDelay);
                    return;
                }
            }

            if (dragged.exerciseIndex === event.event.exerciseIndex &&
                dragged.assignedRoutineId === event.event.assignedRoutineId &&
                dragged.block === event.event.block) return vm.draggingEvent = null;

            vm.loadingDragging = true

            await vm.trackCalendar(vm.$t('history.move', { name: `«${dragged.name}»` }), [dragged.date, event.event.date], async () => {
                if (dragged.assignedRoutineId !== event.event.assignedRoutineId) {
                    await vm.updateBlockExercises(dragged)
                    await vm.onAddExercise(vm.formatPayloadAddExercise(dragged), false)
                }

                await vm.updateBlockExercises(event, true, false, false, dragged)

                await vm.onAddExercise(vm.formatPayloadAddExercise(event.event))
            })

            vm.loadingDragging = false
            vm.dragging = false
            vm.draggingEvent = null
            if (vm.animationFrame) {
                cancelAnimationFrame(vm.animationFrame)
                vm.animationFrame = null
            }
        },
        async deleteEmptyAssignedRoutine(assignedRoutineId) {
            const vm = this
            const docRef = db.collection(vm.collectionName).doc(assignedRoutineId)
            if (vm.collectionName === 'groupAssignedRoutines') {
                let ids = vm.currentRoutines.find(r => r.id === assignedRoutineId)?.assignedRoutinesIds
                if (!ids) {
                    const routineRef = await docRef.get()
                    ids = routineRef.data()?.assignedRoutinesIds || []
                }
                await Promise.all([deleteAssignedRoutinesGroup(ids), docRef.delete()])
            } else {
                await docRef.delete()
            }
        },
        async persistRoutineBlocks(assignedRoutineId, blocks) {
            const vm = this
            const ops = [
                db.collection(vm.collectionName).doc(assignedRoutineId).update({ blocks }),
                pruneRoutineChanges(assignedRoutineId, blocks, vm.collectionName),
            ]
            if (vm.collectionName === 'groupAssignedRoutines') {
                let ids = vm.currentRoutines.find(r => r.id === assignedRoutineId)?.assignedRoutinesIds
                if (!ids) {
                    const routineRef = await db.collection(vm.collectionName).doc(assignedRoutineId).get()
                    ids = routineRef.data()?.assignedRoutinesIds || []
                }
                ops.push(updateAssignedRoutinesGroup(ids, blocks))
            }
            await Promise.all(ops)
        },
        async moveCircuitBlock(draggingEvent, targetEvent, wholeBlock = false) {
            const vm = this
            const sameRoutine = draggingEvent.assignedRoutineId === targetEvent.assignedRoutineId
            if (sameRoutine && draggingEvent.block === targetEvent.block) return

            const sourceRoutine = vm.currentRoutines.find(r => r.id === draggingEvent.assignedRoutineId)
            const hasTarget = vm.currentRoutines.some(r => r.id === targetEvent.assignedRoutineId)
            if (!sourceRoutine || !hasTarget) return

            vm.loadingDragging = true

            const sourceBlocks = clearBlocksAssignedRoutine(sourceRoutine.blocks, false)
            const circuitBlock = sourceBlocks[draggingEvent.block - 1]

            if (sameRoutine) {
                let blocks = [...sourceBlocks]
                blocks.splice(draggingEvent.block - 1, 1)
                blocks.splice(targetEvent.block - 1, 0, circuitBlock)
                blocks = clearBlocksAssignedRoutine(blocks, false)
                await vm.persistRoutineBlocks(draggingEvent.assignedRoutineId, blocks)
                await vm.onAddExercise(vm.formatPayloadAddExercise(draggingEvent))
            } else {
                await vm.updateBlockExercises(draggingEvent, false, false, false, null, wholeBlock)
                await vm.onAddExercise(vm.formatPayloadAddExercise(draggingEvent), false)

                const targetRoutine = vm.currentRoutines.find(r => r.id === targetEvent.assignedRoutineId)
                let blocks = clearBlocksAssignedRoutine(targetRoutine.blocks, false)
                blocks.splice(targetEvent.block, 0, circuitBlock)
                blocks = clearBlocksAssignedRoutine(blocks, false)
                await vm.persistRoutineBlocks(targetEvent.assignedRoutineId, blocks)
                await vm.onAddExercise(vm.formatPayloadAddExercise(targetEvent))
            }

            vm.loadingDragging = false
            if (vm.animationFrame) {
                cancelAnimationFrame(vm.animationFrame)
                vm.animationFrame = null
            }
        },
        async moveExerciseToNewBlock(draggingEvent, targetEvent) {
            const vm = this
            const hasSource = vm.currentRoutines.some(r => r.id === draggingEvent.assignedRoutineId)
            const hasTarget = vm.currentRoutines.some(r => r.id === targetEvent.assignedRoutineId)
            if (!hasSource || !hasTarget) return

            vm.loadingDragging = true

            const newBlock = {
                completed: false,
                duration: 0,
                isCircuit: false,
                exercises: [getPayloadExercise(draggingEvent)]
            }

            const sameRoutine = draggingEvent.assignedRoutineId === targetEvent.assignedRoutineId

            if (sameRoutine) {
                const routine = vm.currentRoutines.find(r => r.id === draggingEvent.assignedRoutineId)
                let blocks = clearBlocksAssignedRoutine(routine.blocks, false)
                const srcIdx = draggingEvent.block - 1
                const tgtIdx = targetEvent.block - 1
                blocks.splice(tgtIdx, 0, newBlock)
                const adjustedSrcIdx = tgtIdx <= srcIdx ? srcIdx + 1 : srcIdx
                blocks[adjustedSrcIdx].exercises.splice(draggingEvent.exerciseIndex, 1)
                blocks = blocks.filter(b => b.exercises.length > 0)
                blocks = clearBlocksAssignedRoutine(blocks, false)
                await vm.persistRoutineBlocks(draggingEvent.assignedRoutineId, blocks)
                await vm.onAddExercise(vm.formatPayloadAddExercise(draggingEvent))
            } else {
                await vm.updateBlockExercises(draggingEvent)
                await vm.onAddExercise(vm.formatPayloadAddExercise(draggingEvent), false)

                const targetRoutine = vm.currentRoutines.find(r => r.id === targetEvent.assignedRoutineId)
                let blocks = clearBlocksAssignedRoutine(targetRoutine.blocks, false)
                blocks.splice(targetEvent.block - 1, 0, newBlock)
                blocks = clearBlocksAssignedRoutine(blocks, false)
                await vm.persistRoutineBlocks(targetEvent.assignedRoutineId, blocks)
                await vm.onAddExercise(vm.formatPayloadAddExercise(targetEvent))
            }

            vm.loadingDragging = false
            if (vm.animationFrame) {
                cancelAnimationFrame(vm.animationFrame)
                vm.animationFrame = null
            }
        },
        async endDragFreeSpace(event) {
            const vm = this
            if (!vm.dragging) { vm.draggingEvent = null; return }
            vm.dragging = false
            if (!vm.draggingEvent || !vm.thisMonth(event.month)) { vm.draggingEvent = null; return }
            const dragged = vm.draggingEvent
            const wholeBlock = vm.blockDragMode
            vm.blockDragMode = false

            // COMPETENCIA soltada en dia libre: mover; si el dia tiene eventos, error
            if (dragged.type === 'competence') {
                if (event.date === dragged.date) { vm.draggingEvent = null; return }
                if (vm.hasEventsToday(event.date)) {
                    vm.toast(vm.$t('routines.competence_only_free_day'), 'error')
                    vm.draggingEvent = null
                    return
                }
                await vm.moveCompetenceToFreeDay(dragged, event.date)
                vm.draggingEvent = null
                return
            }

            if (vm.isCompetenceDay(event.date)) {
                vm.toast(vm.$t('routines.cannot_drop_on_competence'), 'error')
                vm.draggingEvent = null
                return
            }
            if (vm.hasEventsToday(event.date)) return;

            vm.loadingDragging = true

            const moveLabel = dragged.isCircuit || wholeBlock ? vm.blockHandleLabel(dragged) : `«${dragged.name}»`
            await vm.trackCalendar(vm.$t('history.move', { name: moveLabel }), [dragged.date, event.date], async () => {
                let blocks
                if (dragged.isCircuit || wholeBlock) {
                    const sourceRoutine = vm.currentRoutines.find(r => r.id === dragged.assignedRoutineId)
                    const sourceBlocks = clearBlocksAssignedRoutine(sourceRoutine.blocks, false)
                    blocks = [sourceBlocks[dragged.block - 1]]
                } else {
                    blocks = [{
                        completed: false,
                        duration: 0,
                        id: 1,
                        exercises: [getPayloadExercise(dragged)]
                    }]
                }

                let payloadAssignedRoutine = getPayloadBaseAssignedRoutine(`${vm.currentSelection.name} - ${vm.$moment(event.date).format('DD-MM-YYYY')}`, event.date, { coachId: vm.coachId }, blocks, vm.routineNumber)

                if (vm.collectionName === 'assignedRoutines') {
                    payloadAssignedRoutine.studentId = vm.currentSelection.objectID
                } else {
                    payloadAssignedRoutine.teamId = vm.currentSelection.teamId || null
                    payloadAssignedRoutine.groupId = vm.currentSelection.id
                }

                let newAssignedRoutine = await db.collection(vm.collectionName).add(payloadAssignedRoutine)

                // update old assigned routine
                await vm.updateBlockExercises(dragged, false, false, false, null, wholeBlock)
                await vm.onAddExercise({ assignedRoutineId: dragged.assignedRoutineId, date: dragged.date }, false)
                await vm.onAddExercise({ assignedRoutineId: newAssignedRoutine.id, date: event.date })

                // console.log('payloadAssignedRoutine',payloadAssignedRoutine)

                if (vm.collectionName === 'groupAssignedRoutines') {
                    let students = [...vm.currentSelection.students]
                    if (vm.assignedRoutineToReplace.length > 0) {
                        let toDelete = vm.assignedRoutineToReplace.filter(ar => ar.replace).map(ar => {
                            return ar.assignedRoutineId
                        })
                        if (toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)
                        let skippedStudents = vm.assignedRoutineToReplace.filter(ar => !ar.replace).map(ar => {
                            return ar.studentId
                        })
                        if (skippedStudents.length > 0) students = students.filter(st => !skippedStudents.includes(st))
                    }
                    await addAssignedRoutinesGroup({ id: newAssignedRoutine.id, ...payloadAssignedRoutine, date: event.date }, students)
                }
            })

            vm.loadingDragging = false
            vm.draggingEvent = null
            if (vm.animationFrame) {
                cancelAnimationFrame(vm.animationFrame)
                vm.animationFrame = null
            }
        },
        onDragMove(e) {
            const vm = this;
            if (!vm.draggingEvent) return;
            if (!vm.dragging) {
                const dx = Math.abs(e.clientX - vm.startX)
                const dy = Math.abs(e.clientY - vm.startY)
                if (dx < 5 && dy < 5) return;
                vm.dragging = true;
                vm.$nextTick(() => vm.positionGhost(e.clientX, e.clientY))
            }
            if (vm.animationFrame) cancelAnimationFrame(vm.animationFrame)

            vm.animationFrame = requestAnimationFrame(() => vm.positionGhost(e.clientX, e.clientY))
            vm.updateLibraryDropZone(e)
        },
        positionGhost(x, y) {
            const ghost = this.$refs.dragGhost
            if (ghost) ghost.style.transform = `translate(${x - 15}px, ${y - 10}px)`
        },
        onWindowMouseUp() {
            const vm = this;
            const saveTarget = vm.libraryDropActive && vm.dragging && vm.draggingEvent
                ? { event: vm.draggingEvent, wholeBlock: vm.blockDragMode }
                : null
            vm.libraryDropActive = false
            if (saveTarget) vm.openSaveToLibrary(saveTarget)
            window.removeEventListener('mousemove', vm.onDragMove)
            window.removeEventListener('mouseup', vm.onWindowMouseUp)
            vm._hoverTargetUid = null
            vm.clearHighlight()
            vm.$nextTick(() => {
                if (!vm.loadingDragging) {
                    vm.dragging = false
                    vm.draggingEvent = null
                    vm.blockDragMode = false
                }
            })
        },
        onHoverEvent(event) {
            const vm = this
            if (!vm.dragging) return
            const d = vm.draggingEvent
            if (!d) return
            // Los casos invalidos se marcan a nivel dia (onEnterDay)
            if (d.type === 'competence' || event.event.type === 'competence') return
            if (vm._hoverTargetUid === event.event.uid) return
            vm._hoverTargetUid = event.event.uid
            vm.highlightTarget(event.event)
        },
        onLeaveEvent(event) {
            if (this._hoverTargetUid === event.event.uid) {
                this._hoverTargetUid = null
                this.clearEventHighlight()
            }
        },
        onEnterDay(day) {
            const vm = this
            if (!vm.dragging || !vm.draggingEvent) return
            const d = vm.draggingEvent
            const date = day.date
            const invalid = d.type === 'competence'
                ? (date !== d.date && vm.hasEventsToday(date))
                : vm.isCompetenceDay(date)
            if (invalid) {
                vm.clearEventHighlight()
                vm.highlightDayCellByDate(date)
            } else {
                vm.clearDayHighlight()
            }
        },
        onLeaveDay() {
            this.clearDayHighlight()
        },
        highlightTarget(target) {
            const vm = this
            const d = vm.draggingEvent
            if (!d) return

            const root = vm.$refs.calendar && vm.$refs.calendar.$el
            if (!root) return

            const dragsWholeBlock = d.isCircuit || vm.blockDragMode
            const invalid = target.uid === d.uid
                || (dragsWholeBlock && d.assignedRoutineId === target.assignedRoutineId && d.block === target.block)
            if (invalid) return vm.clearEventHighlight()

            const wholeBlock = dragsWholeBlock || target.isCircuit
            const selector = wholeBlock
                ? `[data-block="${target.assignedRoutineId}|${target.block}"]`
                : `[data-uid="${target.uid}"]`
            const els = Array.from(root.querySelectorAll(selector))
            vm.clearEventHighlight()
            if (!els.length) return

            if (wholeBlock) {
                const first = els[0]
                const last = els[els.length - 1]
                const height = last.getBoundingClientRect().bottom - first.getBoundingClientRect().top
                first.style.setProperty('--drop-h', `${height}px`)
                first.classList.add('drop-target-block')
                vm._highlightedEls = [first]
                return
            }

            els.forEach(el => el.classList.add('drop-target-ok'))
            vm._highlightedEls = els
        },
        highlightDayCellByDate(date) {
            const vm = this
            const root = vm.$refs.calendar && vm.$refs.calendar.$el
            if (!root) return
            const evs = vm.eventsByDate.get(date)
            if (!evs || !evs.length) return
            const evEl = root.querySelector(`[data-uid="${evs[0].uid}"]`)
            const cell = evEl && evEl.closest('.v-calendar-weekly__day')
            if (!cell) return
            const label = cell.querySelector('.v-calendar-weekly__day-label')
            cell.style.setProperty('--label-h', (label ? label.offsetHeight : 34) + 'px')
            cell.classList.add('day-drop-invalid')
            vm._highlightedCell = cell
        },
        clearEventHighlight() {
            const els = this._highlightedEls || []
            els.forEach(el => {
                el.classList.remove('drop-target-ok')
                el.classList.remove('drop-target-invalid')
                el.classList.remove('drop-target-block')
                el.style.removeProperty('--drop-h')
            })
            this._highlightedEls = []
        },
        clearDayHighlight() {
            if (this._highlightedCell) {
                this._highlightedCell.classList.remove('day-drop-invalid')
                this._highlightedCell.style.removeProperty('--label-h')
                this._highlightedCell = null
            }
        },
        clearHighlight() {
            this.clearEventHighlight()
            this.clearDayHighlight()
        },

        // Library
        toggleLibrary() {
            this.libraryOpen = !this.libraryOpen
        },
        updateLibraryTop() {
            const vm = this
            const row = vm.$el?.querySelector('.worktype-row') || vm.$el?.querySelector('.cal-header')
            const bar = document.querySelector('.v-app-bar')
            const barBottom = bar ? bar.getBoundingClientRect().bottom : 0
            const rowBottom = row ? row.getBoundingClientRect().bottom : 0
            vm.libraryTop = Math.max(barBottom, rowBottom, 0)
        },
        visibleCalendarDates() {
            const vm = this
            const start = vm.$refs.calendar?.lastStart?.date
            const end = vm.$refs.calendar?.lastEnd?.date
            if (!start || !end) return []
            const dates = []
            const cursor = vm.$moment(start)
            while (cursor.isSameOrBefore(end, 'day')) {
                dates.push(cursor.format('YYYY-MM-DD'))
                cursor.add(1, 'day')
            }
            return dates
        },
        libraryItemLabel(item) {
            return `«${item?.name || ''}»`
        },
        onLibraryItemClick({ type, item }) {
            const vm = this
            const key = type === 'prescriptions' ? 'calendar_library.drag_hint_prescription' : 'calendar_library.drag_hint'
            vm.toast(vm.$t(key, { name: vm.libraryItemLabel(item) }), 'info')
        },
        onLibraryDragStart(payload, event) {
            const vm = this
            if (vm.loadingDragging || vm.dragging || vm.isDayView) return
            vm.libraryDrag = { ...payload }
            window.addEventListener('mousemove', vm.onLibraryDragMove)
            window.addEventListener('mouseup', vm.onLibraryDragEnd)
            vm.$nextTick(() => vm.onLibraryDragMove(event))
        },
        onLibraryDragMove(e) {
            const vm = this
            if (!vm.libraryDrag) return
            const ghost = vm.$refs.libraryGhost
            if (ghost) ghost.style.transform = `translate(${e.clientX - 15}px, ${e.clientY - 10}px)`
            if (vm._libraryFrame) cancelAnimationFrame(vm._libraryFrame)
            const x = e.clientX
            const y = e.clientY
            vm._libraryFrame = requestAnimationFrame(() => vm.previewLibraryDrop(vm.resolveLibraryTarget(x, y)))
        },
        eventByUid(uid) {
            return uid ? this.events.find(ev => String(ev.uid) === String(uid)) || null : null
        },
        resolveLibraryTarget(x, y) {
            const vm = this
            const root = vm.$refs.calendar?.$el
            const el = document.elementFromPoint(x, y)
            if (!root || !el || !root.contains(el) || vm.isDayView) return null
            const event = vm.eventByUid(el.closest('[data-uid]')?.getAttribute('data-uid'))
            if (vm.libraryDrag?.type === 'prescriptions') {
                if (!event || event.type === 'competence' || event.isCircuit) return null
                return { event, date: event.date }
            }
            if (event) return { date: event.date }
            const holder = el.closest('[data-date]') || el.closest('.v-calendar-weekly__day')?.querySelector('[data-date]')
            const date = holder?.getAttribute('data-date')
            return date ? { date } : null
        },
        libraryTargetDates(target) {
            const drag = this.libraryDrag
            if (!target?.date || !drag) return []
            if (drag.type !== 'programs') return [target.date]
            const sessions = drag.item?.assignedRoutines?.length || 1
            return programSessionDates(target.date, defaultProgramWeekdays(target.date, drag.item?.daysPerWeek), sessions)
        },
        previewLibraryDrop(target) {
            const vm = this
            vm.clearLibraryHighlight()
            const root = vm.$refs.calendar?.$el
            if (!target || !vm.libraryDrag || !root) return
            if (vm.libraryDrag.type === 'prescriptions') {
                const els = Array.from(root.querySelectorAll(`[data-uid="${target.event.uid}"]`))
                els.forEach(el => el.classList.add('drop-target-ok'))
                vm._libraryHighlights = els.map(el => ({ el, cls: 'drop-target-ok' }))
                return
            }
            const isBlock = vm.libraryDrag.type === 'blocks'
            vm._libraryHighlights = vm.libraryTargetDates(target).map(date => {
                const holder = root.querySelector(`[data-date="${date}"]`)
                const cell = holder && (holder.closest('.v-calendar-weekly__day') || holder)
                if (!cell) return null
                const ghost = isBlock ? 'calendar_library.ghost_block' : (vm.routineToday(date) ? 'calendar_library.ghost_replace' : 'calendar_library.ghost_add')
                cell.classList.add('cal-lib-target')
                cell.setAttribute('data-ghost', vm.$t(ghost))
                return { el: cell, cls: 'cal-lib-target' }
            }).filter(Boolean)
        },
        clearLibraryHighlight() {
            (this._libraryHighlights || []).forEach(({ el, cls }) => {
                el.classList.remove(cls)
                el.removeAttribute('data-ghost')
            })
            this._libraryHighlights = []
        },
        async onLibraryDragEnd(e) {
            const vm = this
            window.removeEventListener('mousemove', vm.onLibraryDragMove)
            window.removeEventListener('mouseup', vm.onLibraryDragEnd)
            if (vm._libraryFrame) cancelAnimationFrame(vm._libraryFrame)
            const drag = vm.libraryDrag
            const target = drag ? vm.resolveLibraryTarget(e.clientX, e.clientY) : null
            vm.clearLibraryHighlight()
            vm.libraryDrag = null
            if (!drag || !target) return
            if (drag.type !== 'prescriptions' && vm.isCompetenceDay(target.date)) {
                return vm.toast(vm.$t('routines.cannot_drop_on_competence'), 'error')
            }
            const handlers = {
                programs: vm.dropLibraryProgram,
                routines: vm.dropLibraryRoutine,
                blocks: vm.dropLibraryBlock,
                prescriptions: vm.dropLibraryPrescription,
            }
            try {
                await handlers[drag.type](drag.item, target)
            } catch (error) {
                console.error('[calendar library] drop', error)
                vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
            } finally {
                vm.loadingDragging = false
            }
        },
        async libraryDoc(type, item) {
            const id = item?.objectID || item?.id
            if (!id) return null
            if (item.fromFirestore) {
                const data = cloneDeep(item)
                delete data.fromFirestore
                return data
            }
            const snap = await db.collection(type).doc(id).get()
            return snap.exists ? { ...snap.data(), id: snap.id, objectID: snap.id } : null
        },
        async dropLibraryProgram(item, { date }) {
            const vm = this
            vm.loadingDragging = true
            const program = await vm.libraryDoc('programs', item)
            vm.loadingDragging = false
            if (!program?.assignedRoutines?.length) return vm.toast(vm.$t('programs.no_routines_added'), 'error')
            vm.applyVisibleDates = vm.visibleCalendarDates()
            vm.$refs.applyProgram.show(program, date)
        },
        async onApplyProgram({ program, dates }) {
            const vm = this
            vm.applyingProgram = true
            try {
                await vm.trackCalendar(vm.$t('history.apply_program', { name: vm.libraryItemLabel(program) }), dates, () => assignProgramDates({
                    program,
                    coachId: vm.coachId,
                    collection: vm.collectionName,
                    selections: [vm.currentSelection],
                    dates,
                    routineNumber: vm.routineNumber,
                }))
                await vm.refreshCalendarEvents()
                vm.$refs.applyProgram.hide()
                const visible = new Set(vm.visibleCalendarDates())
                const inView = dates.filter(date => visible.has(date)).length
                if (inView < dates.length) {
                    vm.toast(vm.$t('calendar_library.sessions_summary', { total: dates.length, inView, later: dates.length - inView }), 'info')
                }
            } catch (error) {
                console.error('[calendar library] program', error)
                vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
            } finally {
                vm.applyingProgram = false
            }
        },
        async dropLibraryRoutine(item, { date }) {
            const vm = this
            vm.loadingDragging = true
            const routine = await vm.libraryDoc('routines', item)
            if (!routine) return
            await vm.trackCalendar(vm.$t('history.apply_routine', { name: vm.libraryItemLabel(routine) }), [date], () => assignRoutineToSelection({
                routine,
                collection: vm.collectionName,
                selection: vm.currentSelection,
                dates: [date],
                routineNumber: vm.routineNumber,
            }))
            await vm.refreshCalendarEvents()
        },
        async dropLibraryBlock(item, { date }) {
            const vm = this
            vm.loadingDragging = true
            const doc = await vm.libraryDoc('blocks', item)
            const block = doc?.block
            if (!block) return
            const routine = vm.routineToday(date)
            await vm.trackCalendar(vm.$t('history.add_library_block', { name: vm.libraryItemLabel(doc) }), [date], async () => {
                if (routine?.id && routine.type !== 'competence') {
                    const blocks = clearBlocksAssignedRoutine([...(routine.blocks || []), block], false)
                    await vm.persistRoutineBlocks(routine.id, blocks)
                } else {
                    await assignRoutineToSelection({
                        routine: { coachId: vm.coachId, blocks: [block] },
                        collection: vm.collectionName,
                        selection: vm.currentSelection,
                        dates: [date],
                        routineNumber: vm.routineNumber,
                    })
                }
            })
            await db.collection('blocks').doc(doc.id).update({ used: FieldValue.increment(1) })
            await vm.refreshCalendarEvents()
        },
        async dropLibraryPrescription(item, { event }) {
            const vm = this
            const target = vm.eventByUid(event.uid)
            if (!target) return
            vm.loadingDragging = true
            const prescription = await vm.libraryDoc('prescriptions', item)
            if (!prescription?.config) return
            const multiWeek = (prescription.weeks || 1) > 1
            Object.assign(target, {
                config: cloneDeep(prescription.config),
                time: prescription.time || '',
                hasTime: !!prescription.time,
                eachSide: prescription.eachSide || false,
                painScale: prescription.painScale || false,
                weightMode: prescription.weightMode || false,
                commentary: prescription.commentary || '',
                maxWeight: prescription.maxWeight || target.maxWeight || null,
                prescription: multiWeek ? prescription.id : null,
                currentWeek: multiWeek ? 1 : target.currentWeek,
            })
            const label = vm.$t('history.apply_prescription', {
                prescription: exerciseSetsLabel({ config: prescription.config }, null, vm.$t),
                name: vm.libraryItemLabel(target),
            })
            await vm.trackCalendar(label, [target.date], async () => {
                await vm.updateBlockExercises(target, false, false, true)
                await vm.onAddExercise(target)
            })
            await db.collection('prescriptions').doc(prescription.id).update({ used: FieldValue.increment(1) })
        },
        updateLibraryDropZone(e) {
            const vm = this
            const event = vm.draggingEvent
            if (!vm.libraryOpen || !vm.dragging || !event || event.type === 'competence') {
                vm.libraryDropActive = false
                return
            }
            const rect = vm.$refs.library?.$el?.getBoundingClientRect()
            const over = !!rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom
            vm.libraryDropActive = over
            vm.libraryDropLabel = over ? (vm.blockDragMode || event.isCircuit ? vm.$tc('common.blocks', 1) : vm.$t('common.prescription')) : ''
        },
        openSaveToLibrary({ event, wholeBlock }) {
            const vm = this
            const routine = vm.currentRoutines.find(r => r.id === event.assignedRoutineId)
            if (!routine) return
            if (wholeBlock || event.isCircuit) {
                const block = clearBlocksAssignedRoutine(routine.blocks, false)[event.block - 1]
                if (!block) return
                return vm.$refs.saveToLibrary.show('blocks', block, block.name || vm.blockHandleLabel(event))
            }
            vm.$refs.saveToLibrary.show('prescriptions', event, `${event.name} ${exerciseSetsLabel(event, null, vm.$t)}`.trim())
        },
        onDayDrag(date, event) {
            const vm = this
            const routine = vm.routineToday(date)
            if (!routine || routine.type === 'competence' || vm.dragging || vm.libraryDrag || vm.loadingDragging) return
            vm.libraryOpen = true
            vm.dayDrag = { date, name: routine.name ? routine.name.split(' - ')[0] : vm.$t('common.routine') }
            window.addEventListener('mousemove', vm.onDayDragMove)
            window.addEventListener('mouseup', vm.onDayDragEnd)
            vm.$nextTick(() => vm.onDayDragMove(event))
        },
        onDayDragMove(e) {
            const vm = this
            if (!vm.dayDrag) return
            const ghost = vm.$refs.libraryGhost
            if (ghost) ghost.style.transform = `translate(${e.clientX - 15}px, ${e.clientY - 10}px)`
            const rect = vm.$refs.library?.$el?.getBoundingClientRect()
            const over = !!rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom
            vm.libraryDropActive = over
            vm.libraryDropLabel = over ? vm.$t('common.routine') : ''
        },
        onDayDragEnd() {
            const vm = this
            window.removeEventListener('mousemove', vm.onDayDragMove)
            window.removeEventListener('mouseup', vm.onDayDragEnd)
            const over = vm.libraryDropActive
            const date = vm.dayDrag?.date
            vm.libraryDropActive = false
            vm.dayDrag = null
            if (over && date) vm.saveDayToLibrary(date)
        },
        saveDayToLibrary(date) {
            const vm = this
            const routine = vm.routineToday(date)
            if (!routine || routine.type === 'competence') return
            vm.$refs.saveToLibrary.show('routines', routine, routine.name ? routine.name.split(' - ')[0] : '')
        },
        async onLibrarySaved({ type, id, name, payload }) {
            const vm = this
            vm.libraryNewIds = [...vm.libraryNewIds, id]
            vm.libraryOpen = true
            await vm.$refs.library?.showTab(type)
            const ref = db.collection(type).doc(id)
            pushHistory(CALENDAR_HISTORY, {
                label: vm.$t('history.save_to_library', { name: `«${name}»` }),
                undo: async () => {
                    await ref.delete()
                    await vm.$refs.library?.load(true)
                },
                redo: async () => {
                    await ref.set(payload)
                    await vm.$refs.library?.load(true)
                },
            })
        },

        // Event Actions
        async duplicateEvent(event) {
            const vm = this;
            vm.loadingDragging = true
            await vm.trackCalendar(vm.$t('history.duplicate', { name: `«${event.name}»` }), [event.date], async () => {
                await vm.updateBlockExercises(event, true, true, false, event)
                await vm.onAddExercise(vm.formatPayloadAddExercise(event))
            })
            vm.loadingDragging = false
        },
        onAddSet(event) {
            const vm = this
            const eventIndex = vm.events.findIndex(ev => ev.uid === event.event.uid)
            if (eventIndex < 0) return
            const sets = vm.events[eventIndex].config?.sets
            if (!sets?.length) return
            sets.push(cloneDeep(sets[sets.length - 1]))
            vm.schedulePersistEdit(vm.events[eventIndex], 'sets')
        },
        onRemoveSet(event) {
            const vm = this
            const eventIndex = vm.events.findIndex(ev => ev.uid === event.event.uid)
            if (eventIndex < 0) return
            const sets = vm.events[eventIndex].config?.sets
            if (!sets?.length) return
            if (sets.length <= 1) return vm.toast(vm.$t('common.cannot_delete_last_set'), 'error')
            sets.splice(sets.length - 1, 1)
            vm.schedulePersistEdit(vm.events[eventIndex], 'sets')
        },
        onAdjustReps(event, delta) {
            const vm = this
            const eventIndex = vm.events.findIndex(ev => ev.uid === event.event.uid)
            if (eventIndex < 0) return
            const sets = vm.events[eventIndex].config?.sets
            if (!sets?.length) return
            sets.forEach(set => {
                if (set.data?.[0]) {
                    set.data[0].value = Math.max(1, (Number(set.data[0].value) || 0) + delta)
                }
            })
            vm.schedulePersistEdit(vm.events[eventIndex], 'reps')
        },
        schedulePersistEdit(event, kind = 'sets') {
            const vm = this
            event.historyKind = kind
            const key = event.uid
            const pending = vm._pendingEdits[key]
            if (pending) clearTimeout(pending.timer)
            vm._pendingEdits[key] = { event, timer: setTimeout(() => vm.runPendingEdit(key), 500) }
        },
        runPendingEdit(key) {
            const vm = this
            const pending = vm._pendingEdits[key]
            if (!pending) return Promise.resolve()
            clearTimeout(pending.timer)
            delete vm._pendingEdits[key]
            return vm.persistEventEdit(pending.event)
        },
        flushPendingEdits() {
            const vm = this
            return Promise.all(Object.keys(vm._pendingEdits).map(key => vm.runPendingEdit(key)))
        },
        async persistEventEdit(event) {
            const vm = this
            const kind = event.historyKind || 'sets'
            const label = vm.$t(kind === 'reps' ? 'history.reps_of' : 'history.sets_of', { name: `«${event.name}»` })
            const key = `${kind}|${event.assignedRoutineId}|${event.block}|${event.exerciseIndex}`
            await vm.trackCalendar(label, [event.date], async () => {
                await vm.updateBlockExercises(event, false, false, true)
                await vm.onAddExercise(event)
            }, key)
        },

        // Set current selection
        findByName(list, value) {
            if (value?.name) return value
            if (typeof value !== 'string') return null
            const term = value.toLowerCase()
            return list?.find(item => item.name?.toLowerCase() === term) || null
        },
        setCurrentStudent(student) {
            const vm = this
            vm.currentStudent = vm.findByName(vm.students, student)
            vm.currentWeekOption = vm.currentStudent?.weekStatusOption || 'week'
            vm.currentWeekStatus = vm.currentStudent?.weekStatus || 'to_be_done'
        },
        setCurrentGroup(group) {
            const vm = this
            vm.currentGroup = vm.findByName(vm.groups, group)
            vm.currentWeekOption = vm.currentGroup?.weekStatusOption || 'week'
            vm.currentWeekStatus = vm.currentGroup?.weekStatus || 'to_be_done'
        },
        setCurrentTeam(team) {
            const vm = this
            vm.currentTeam = vm.findByName(vm.teams, team)
            vm.currentTeamGroup = null
        },
        setCurrentTeamGroup(teamGroup) {
            const vm = this
            vm.currentTeamGroup = vm.findByName(vm.teamGroups, teamGroup)
            vm.currentWeekOption = vm.currentTeamGroup?.weekStatusOption || 'week'
            vm.currentWeekStatus = vm.currentTeamGroup?.weekStatus || 'to_be_done'
        },
        resetTeam() {
            const vm = this
            vm.currentTeam = null
            vm.currentTeamGroup = null
        },

        // Copy routines
        showCopyRoutine(date) {
            const vm = this
            vm.$refs.copy.show(date)
        },
        onBeforeCopy(dates) {
            return this.beginCalendarTrack('copy', this.$t('history.duplicate_routine'), dates)
        },
        async onCopyRoutine(assignedRoutines) {
            const vm = this
            // console.log('payload copied', assignedRoutines)
            if (assignedRoutines.length > 0) {
                const copyDates = []
                for (const routine of assignedRoutines) {
                    let blocks = await getMergedBlocks(routine.blocks)
                    vm.pushToEvents(blocks, routine.rawDate.full, routine, routine.routineNumber)
                    if (routine.rawDate?.full) copyDates.push(routine.rawDate.full)
                }
                const start = `${vm.currentYear}-${vm.currentMonth}-01`
                const end = copyDates.sort().slice(-1)[0] || null
                await vm.loadRoutines(start, end)
            }
            await vm.endCalendarTrack('copy')
            vm.toast(vm.$t('routines.copied'))
        },
        async loadAssignedRoutinesInSameDate(date, isDraggingEvent = false, addBlock = false) {
            const vm = this
            let assignedRoutinesInSameDate = []
            let students = vm.currentGroup?.students || null
            if (!students) students = vm.currentTeamGroup?.students || []

            const studentIds = (students || []).filter(Boolean)
            if (studentIds.length) {
                const chunks = []
                for (let i = 0; i < studentIds.length; i += 10) chunks.push(studentIds.slice(i, i + 10))
                const snapshots = await Promise.all(
                    chunks.map(chunk =>
                        db.collection('assignedRoutines')
                            .where('studentId', 'in', chunk)
                            .where('rawDate.full', '==', date)
                            .where('routineNumber', '==', vm.routineNumber)
                            .get()
                    )
                )
                const existing = snapshots.flatMap(snap =>
                    snap.docs.map(d => ({ id: d.id, ...d.data() }))
                )
                const missingNameIds = [...new Set(
                    existing.map(r => r.studentId).filter(id => !vm.students.some(s => s.objectID === id))
                )]
                const extra = missingNameIds.length
                    ? await getAllByIds('users', missingNameIds)
                    : []
                const nameOf = (id) => {
                    const fromCalendar = vm.students.find(s => s.objectID === id)
                    if (fromCalendar?.name) return fromCalendar.name
                    const fromExtra = extra.find(s => s.objectID === id)
                    return fromExtra?.name || ''
                }
                existing.forEach(r => {
                    const name = nameOf(r.studentId)
                    if (!name) return
                    assignedRoutinesInSameDate.push({
                        studentId: r.studentId,
                        studentName: name,
                        assignedRoutineId: r.id,
                        replace: false,
                    })
                })
            }

            if (assignedRoutinesInSameDate.length > 0) {
                vm.assignedRoutinesInSameDate = assignedRoutinesInSameDate
                vm.$refs.replaceRoutines?.show(date)
            } else if (!isDraggingEvent) {
                if (vm.isCompetence) {
                    vm.$refs.competence?.show(date)
                    vm.isCompetence = false
                } else {
                    if (vm.isDayView) {
                        vm.$refs.dayCalendarContent?.saveRoutine()
                    } else {
                        addBlock ?
                            vm.$refs.addBlockDialog?.show(date) :
                            vm.$refs.addExerciseDialog?.show(date, null)
                    }
                }
            }
        },
        onConfirmReplace({ date, toReplace }) {
            const vm = this
            vm.assignedRoutineToReplace = toReplace
            if (vm.isCompetence) {
                vm.$refs.competence.show(date)
                vm.isCompetence = false
            } else if (vm.isDayView) {
                vm.$refs.dayCalendarContent.saveRoutine()
            } else {
                vm.$refs.addExerciseDialog.show(date, null)
            }
        },
        // Copy week
        showCopyWeek(date) {
            const vm = this
            vm.currentDate = date
            vm.$refs.copyWeek.show()
        },
        async onConfirmCopyWeek() {
            const vm = this
            await vm.withLoading(async () => {
            const weekStart = vm.$moment(vm.currentDate).startOf('week')
            const targetDates = [7, 8, 9, 10, 11, 12, 13].map(day => weekStart.clone().add(day, 'day').format('YYYY-MM-DD'))
            await vm.trackCalendar(vm.$t('history.duplicate_week'), targetDates, async () => {
                let startDate = vm.$moment(vm.currentDate).startOf('week').add(-1, 'day')
                let uniqueRoutines = uniqueDayRoutine(vm.currentRoutines).filter(routine => routine.routineNumber === vm.routineNumber && routine.type !== 'competence')
                let weekRoutines = []
                for (let i = 0; i < 7; i++) {
                    let currentDate = startDate.add(1, 'day').format('YYYY-MM-DD')
                    let foundRoutine = uniqueRoutines.find(routine => routine.rawDate.full === currentDate)
                    if (foundRoutine) weekRoutines.push(foundRoutine)
                }

                let collection = vm.currentGroupBy === 'students' ? 'assignedRoutines' : 'groupAssignedRoutines'
                let assignedRoutines = []

                const usedPrescriptionIds = [...new Set(
                    weekRoutines
                        .flatMap(routine => routine.blocks)
                        .flatMap(block => block.exercises)
                        .filter(exercise => !!exercise.prescription)
                        .map(exercise => exercise.prescription)
                )];

                let prescriptions = []
                if (usedPrescriptionIds.length > 0) {
                    let filter = usedPrescriptionIds.map(id => `objectID:"${id}"`).join(' OR ')
                    let response = await PrescriptionIndex.search('', {
                        filters: filter,
                        hitsPerPage: 1000,
                    })
                    prescriptions = response.hits
                }

                for (let routine of weekRoutines) {
                    const date = vm.$moment(routine.rawDate.full).add(7, 'day')
                    const dateFormatted = date.format('YYYY-MM-DD')
                    const routineName = `${routine.name.split(' - ')[0]} - ${date.format('DD-MM-YYYY')}`
                    let blocks = clearBlocksAssignedRoutine(routine.blocks)

                    if (prescriptions.length > 0) {
                        blocks = getBlockNextPrescription(blocks, prescriptions)
                    }

                    let payload = getPayloadBaseAssignedRoutine(routineName, dateFormatted, routine, blocks, vm.routineNumber)
                    payload.routineNumber = vm.routineNumber
                    payload.coachId = vm.coachId

                    if (vm.currentGroupBy === 'students') {
                        payload.studentId = routine.studentId
                    } else {
                        payload.groupId = routine.groupId
                        payload.teamId = routine.teamId || null
                    }

                    let currentStudents = payload.studentId ? [payload.studentId] : vm.currentSelection?.students || []
                    vm.events = vm.events.filter(event => event.date !== dateFormatted)

                    await Promise.all(currentStudents.map(studentId =>
                        deleteAssignedRoutineStudentInDate(studentId, dateFormatted, vm.routineNumber)
                    ))

                    let assignedRoutineCreated = await db.collection(collection).add(payload)

                    if (collection === 'groupAssignedRoutines') await addAssignedRoutinesGroup({ id: assignedRoutineCreated.id, ...payload, date: dateFormatted }, currentStudents)

                    assignedRoutines.push({
                        ...payload,
                        date: dateFormatted,
                        id: assignedRoutineCreated.id
                    })
                }

                await vm.onCopyRoutine(assignedRoutines)
            })

            vm.currentDate = null
            if (vm.$refs.copyWeek) vm.$refs.copyWeek.hide()
            }, 'loadingCopyWeek')
        },

        // Delete routines
        showDeleteRoutine(date) {
            const vm = this
            let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
            let findDate = vm.dates.find(d => d.date === dateFormatted)
            let assignedRoutineId = findDate?.assignedRoutineId || null

            if (!assignedRoutineId) return vm.toast(vm.$t('routines.error_with_routine'), 'error')

            vm.confirmDeleteExercise = false
            vm.confirmTitle = vm.$t('routines.delete')
            vm.confirmContent = vm.$t('routines.confirm_delete')
            vm.currentEvent = { assignedRoutineId: assignedRoutineId, date: date }
            vm.$refs.confirm.show()
        },
        async onDeleteRoutine() {
            const vm = this
            await vm.withLoading(async () => {
                await vm.trackCalendar(vm.$t('history.empty_routine'), [vm.currentEvent.date], async () => {
                    const docRef = db.collection(vm.collectionName).doc(vm.currentEvent.assignedRoutineId)
                    if (vm.collectionName === 'groupAssignedRoutines') {
                        let ids = vm.currentRoutines.find(r => r.id === vm.currentEvent.assignedRoutineId)?.assignedRoutinesIds
                        if (!ids) {
                            const groupRoutineRef = await docRef.get()
                            ids = groupRoutineRef.data()?.assignedRoutinesIds || []
                        }
                        await Promise.all([deleteAssignedRoutinesGroup(ids), docRef.delete()])
                    } else {
                        await docRef.delete()
                    }
                    await vm.loadRoutines(vm.$moment(vm.currentEvent.date).format('YYYY-M-DD'))
                    vm.events = vm.events.filter(event => event.assignedRoutineId !== vm.currentEvent.assignedRoutineId);
                })
                vm.$refs.confirm?.hide()
                vm.currentEvent = null
            }, 'loadingDelete')
        },
        // Delete exercise
        showDeleteEvent({ event }) {
            const vm = this
            if (!event) return
            vm.dragging = false
            vm.draggingEvent = null

            vm.currentEvent = {
                ...event,
                hasMoreExercisesInBlock: vm.events.filter(e => e.date === event.date && event.routineNumber === vm.routineNumber && e.block === event.block).length > 1
            }
            vm.confirmDeleteExercise = true
            vm.confirmTitle = vm.currentEvent.isCircuit ? vm.$t('exercises.delete_circuit') : vm.$t('exercises.delete_exercise')
            vm.confirmContent = vm.currentEvent.isCircuit ? vm.$t('exercises.confirm_delete_circuit') : vm.$t('exercises.confirm_delete_exercise', { exercise: vm.currentEvent?.name || '' })
            vm.$refs.confirm.show()
        },
        async onDeleteExercise() {
            const vm = this
            await vm.withLoading(async () => {
                const current = vm.currentEvent
                const deleteLabel = current.isCircuit ? vm.blockHandleLabel(current) : `«${current.name}»`
                await vm.trackCalendar(vm.$t('history.delete', { name: deleteLabel }), [current.date], async () => {
                    let arBlocks = await vm.updateBlockExercises(vm.currentEvent)

                    // CLEAN EVENTS TODAY AND PUSH AGAIN
                    vm.events = vm.events.filter(event => !(event.date === vm.currentEvent.date && event.assignedRoutineId === vm.currentEvent.assignedRoutineId))
                    let blocks = await getMergedBlocks(arBlocks)
                    vm.pushToEvents(blocks, vm.currentEvent.date, vm.currentEvent, vm.routineNumber)
                    await vm.loadRoutines(vm.currentEvent.date)
                })
                vm.$refs.confirm?.hide()
                vm.currentEvent = null
            }, 'loadingDelete')
        },

        // Save routines/programs/block/name
        showSaveRoutineDialog(date) {
            const vm = this
            let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
            let findDate = vm.dates.find(d => d.date === dateFormatted)
            let assignedRoutine = { ...vm.currentRoutines.find(routine => routine.id === findDate?.assignedRoutineId || null) }
            vm.$refs.saveRoutine.show(assignedRoutine)
        },
        showSaveProgramDialog(date) {
            const vm = this
            vm.$refs.saveProgram.show(date)
        },
        onProgramCreated(id) {
            const vm = this
            if (!id) return
            vm.pushRoute({ name: `${vm.accountType}.routines.program.detail`, params: { uid: id } })
        },
        showSaveBlockDialog(date) {
            const vm = this
            let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
            let findDate = vm.dates.find(d => d.date === dateFormatted)
            let assignedRoutine = { ...vm.currentRoutines.find(routine => routine.id === findDate?.assignedRoutineId || null) }
            vm.$refs.saveBlock.show(assignedRoutine)
        },
        showChangeRoutineName(date) {
            const vm = this
            let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
            let findDate = vm.dates.find(d => d.date === dateFormatted)
            let assignedRoutine = vm.currentRoutines.find(routine => routine.id === findDate?.assignedRoutineId)
            if (!assignedRoutine?.id) return
            vm.$refs.changeRoutineName.show({ ...assignedRoutine })
        },
        async onSaveRoutineName(payload) {
            const vm = this;
            const date = vm.currentRoutines.find(routine => routine.id === payload.id)?.rawDate?.full
            await vm.trackCalendar(vm.$t('history.rename_routine'), [date], () => db.collection(vm.collectionName).doc(payload.id).update({
                name: payload.name
            }))
            let index = vm.currentRoutines.findIndex(routine => routine.id === payload.id)
            vm.currentRoutines[index].name = payload.name
            vm.toast(vm.$t('common.name_saved'))
        },

        // Add exercise/block
        async showAddExercise(date) {
            const vm = this
            vm.beginCalendarTrack('dialog', vm.$t('history.add_exercise'), [date])
            vm.loadingDragging = true
            let events = vm.events.filter(event => event.date === date && event.routineNumber === vm.routineNumber)
            let lastEventToday = events.length > 0 && events[events.length - 1] || null
            let groupHasStudents = vm.currentGroup?.students?.length > 0 || vm.currentTeamGroup?.students?.length > 0
            !lastEventToday && groupHasStudents ?
                await vm.loadAssignedRoutinesInSameDate(date) :
                vm.$refs.addExerciseDialog.show(date, lastEventToday)
            vm.loadingDragging = false
        },
        showEvent({ event }) {
            const vm = this
            vm.beginCalendarTrack('dialog', vm.$t('history.edit', { name: `«${event.name}»` }), [event.date])
            event.hasMoreExercisesInBlock = vm.events.filter(e => e.date === event.date && event.routineNumber === vm.routineNumber && e.block === event.block).length > 1
            let lastBlock = vm.events.filter(e => e.date === event.date && event.routineNumber === vm.routineNumber).pop() || event
            event.lastBlock = lastBlock.block
            if (event.isCircuit) {
                const routine = vm.currentRoutines.find(r => r.id === event.assignedRoutineId)
                if (!vm.$refs.createCircuitDialog || !routine?.blocks) return
                return vm.$refs.createCircuitDialog.setEditingModeFromCalendar(event, routine)
            }
            if (!vm.$refs.addExerciseDialog) return
            vm.$refs.addExerciseDialog.setEditingMode(event)
        },
        async showAddBlock(date, isNew = false) {
            const vm = this
            vm.beginCalendarTrack('dialog', vm.$t('history.add_block'), [date])
            vm.loadingDragging = true
            let groupHasStudents = vm.currentGroup?.students?.length > 0 || vm.currentTeamGroup?.students?.length > 0
            isNew && groupHasStudents ?
                await vm.loadAssignedRoutinesInSameDate(date, false, true) :
                vm.$refs.addBlockDialog.show(date)
            vm.loadingDragging = false
        },
        async onDialogSaved(payload) {
            const vm = this
            await vm.onAddExercise(payload)
            await vm.endCalendarTrack('dialog')
        },
        async onAddExercise(payload, reload = true) {
            const vm = this
            let arBlocksRef = await db.collection(vm.collectionName).doc(payload.assignedRoutineId).get()
            vm.events = vm.events.filter(event => !(event.date === payload.date && event.assignedRoutineId === payload.assignedRoutineId))
            if (arBlocksRef.exists) {
                let blocks = await getMergedBlocks(arBlocksRef.data().blocks)
                let routine = { id: payload.assignedRoutineId, routineId: payload.routineId || null }
                vm.pushToEvents(blocks, payload.date, routine, vm.routineNumber)
            }
            if (reload) await vm.loadRoutines(payload.date)
        },
        switchRoutine() {
            const vm = this
            if (vm.routineNumber === 1) vm.routineNumber = 2
            else vm.routineNumber = 1
        },
        onSaveContent(date) {
            const vm = this;
            vm.savingContent = true
            let currentRoutine = vm.currentRoutines.find(routine => routine.rawDate.full === date && routine.routineNumber === vm.routineNumber) || null
            if (!currentRoutine) {
                let groupHasStudents = vm.currentGroup?.students?.length > 0 || vm.currentTeamGroup?.students?.length > 0
                groupHasStudents ? vm.loadAssignedRoutinesInSameDate(vm.focus) : vm.$refs.dayCalendarContent.saveRoutine()
            } else {
                vm.$refs.dayCalendarContent.saveRoutine()
            }
            vm.savingContent = false
        },

        //Lock days
        showLockDay(dates) {
            const vm = this
            vm.$refs.lockDaysDialog.show(dates)
        },

        // Routines
        routineToday(date) {
            return this.routinesByDate.get(date) || null
        },
        changesForEvent(event) {
            if (!event?.assignedRoutineId) return null
            const byId = studentChangeKey(event.block, event)
            return (byId && this.studentChanges.get(`${event.assignedRoutineId}|${byId}`))
                || this.studentChanges.get(`${event.assignedRoutineId}|${event.block}|${event.name}`)
                || null
        },
        showStudentVideo(change) {
            const vm = this
            if (!change?.url) return
            vm.studentVideo = change.url
            vm.$nextTick(() => vm.$refs.studentVideoDialog.show(false))
        },

        // Notes
        noteToday(date) {
            return this.notesByDate.get(date) || null
        },
        showNoteDialog(date = null) {
            const vm = this
            let currentDate = date
            let currentNote = vm.routineNotes?.find(note => note.rawDate.full === currentDate && note.routineNumber === vm.routineNumber) || null
            if (!!date?.rawDate?.full) {
                currentDate = date.rawDate.full
                currentNote = date
            }

            vm.$refs.notes.show(currentNote, currentDate)
        },
        async onSaveNotes(date) {
            await this.loadRoutineNotes(date)
        },

        // Competence
        async showAddCompetenceDialog(date) {
            const vm = this;
            if (!vm.requirePlan('competences')) return
            vm.beginCalendarTrack('competence', vm.$t('history.add_competence'), [date])
            vm.isCompetence = true
            vm.loadingDragging = true
            const groupHasStudents = vm.currentGroup?.students?.length > 0 || vm.currentTeamGroup?.students?.length > 0
            groupHasStudents ?
                await vm.loadAssignedRoutinesInSameDate(date) :
                vm.$refs.competence.show(date)
            vm.loadingDragging = false
        },
        showCompetence({ event }) {
            const vm = this;
            vm.beginCalendarTrack('competence', vm.$t('history.edit_competence'), [event.date])
            vm.$refs.competence.show(event.startDate, event)
        },
        async onCompetenceDialogSaved(payload) {
            const vm = this
            await vm.onSaveCompetence(payload)
            await vm.endCalendarTrack('competence')
        },
        async onSaveCompetence(payload) {
            const vm = this;
            vm.events = vm.events.filter(event => event.assignedRoutineId !== payload.assignedRoutineId)
            vm.pushToEvents(null, payload.date, payload, vm.routineNumber)
            await vm.loadRoutines(payload.date)
        },
        async moveCompetenceToFreeDay(competenceEvent, targetDate) {
            const vm = this
            vm.loadingDragging = true

            await vm.trackCalendar(vm.$t('history.move_competence'), [competenceEvent.date, targetDate], async () => {
                const payload = getPayloadBaseAssignedCompetence(
                    competenceEvent.versus,
                    competenceEvent.minutes || 0,
                    targetDate,
                    vm.coachId,
                    vm.routineNumber
                )
                if (vm.collectionName === 'assignedRoutines') {
                    payload.studentId = vm.currentSelection.objectID
                } else {
                    payload.teamId = vm.currentSelection.teamId || null
                    payload.groupId = vm.currentSelection.id
                }

                // borrar la competencia original
                if (vm.collectionName === 'groupAssignedRoutines') {
                    const oldRef = await db.collection(vm.collectionName).doc(competenceEvent.assignedRoutineId).get()
                    if (oldRef.exists) await deleteAssignedRoutinesGroup(oldRef.data().assignedRoutinesIds)
                }
                await db.collection(vm.collectionName).doc(competenceEvent.assignedRoutineId).delete()

                // crear en la nueva fecha
                const newDoc = await db.collection(vm.collectionName).add(payload)
                if (vm.collectionName === 'groupAssignedRoutines') {
                    await addAssignedCompetenceGroup({ id: newDoc.id, ...payload, date: targetDate }, vm.currentSelection.students || [])
                }

                // actualizar eventos y rutinas
                vm.events = vm.events.filter(e => e.assignedRoutineId !== competenceEvent.assignedRoutineId)
                await vm.loadRoutines(competenceEvent.date)
                await vm.onSaveCompetence({
                    assignedRoutineId: newDoc.id,
                    date: targetDate,
                    versus: competenceEvent.versus,
                    minutes: competenceEvent.minutes || 0,
                    type: 'competence',
                })
            })

            vm.loadingDragging = false
            if (vm.animationFrame) {
                cancelAnimationFrame(vm.animationFrame)
                vm.animationFrame = null
            }
        },
        showDeleteCompetence({ event }) {
            const vm = this;
            vm.currentEvent = event
            vm.$refs.deleteCompetence.show()
        },
        async onDeleteCompetence() {
            const vm = this
            if (vm.loading) return;
            await vm.withLoading(async () => {
                await vm.trackCalendar(vm.$t('history.delete_competence'), [vm.currentEvent.date], async () => {
                    if (vm.collectionName === 'groupAssignedRoutines') {
                        let groupRoutineRef = await db.collection(vm.collectionName).doc(vm.currentEvent.assignedRoutineId).get()
                        await deleteAssignedRoutinesGroup(groupRoutineRef.data().assignedRoutinesIds)
                    }

                    await db.collection(vm.collectionName).doc(vm.currentEvent.assignedRoutineId).delete()
                    await vm.loadRoutines(vm.$moment(vm.currentEvent.date).format('YYYY-M-DD'))
                    vm.events = vm.events.filter(event => event.assignedRoutineId !== vm.currentEvent.assignedRoutineId)
                })
                vm.$refs.deleteCompetence?.hide()
                vm.currentEvent = null
            }, 'loadingDelete')
        },

        // Questionnaire
        showAssignQuestionaire(date) {
            const vm = this;
            let event = vm.events.find(event => event.date === date && event.routineNumber === vm.routineNumber)
            vm.$refs.questionnaire.show(event)
        },
        useQuestionnaireForDay(questionnaire) {
            const vm = this;
            let event = vm.events.find(event => event.date === vm.focus && event.routineNumber === vm.routineNumber)
            if (!event) return vm.toast(vm.$t('routines.save_before_questionnaire'), 'error')
            vm.$refs.questionnaire.show(event, questionnaire)
        },
        async onAssignQuestionnaire(payload) {
            const vm = this;
            vm.events
                .filter(event => event.assignedRoutineId === payload.assignedRoutineId)
                .forEach(event => {
                    event.assignedQuestionnaireId = payload.questionnaireId
                })

            vm.updateAssignedQuestionnaireId({
                groupBy: vm.currentGroupBy,
                routineId: payload.assignedRoutineId,
                questionnaireId: payload.questionnaireId
            })
        },

        // Week options/status
        getIconWeek(status) {
            if (status === 'finished') return { icon: 'mdi-check', color: '#4cb351' }
            if (status === 'incomplete') return { icon: 'mdi-pencil', color: '#ac7d1d' }
            if (status === 'to_be_done') return { icon: 'mdi-close', color: '#9a2d2d' }
            return { icon: '', color: '#000000' }
        },
        async onChangeWeekOption(weekOption) {
            const vm = this
            const selection = vm.currentSelection
            const groupBy = vm.currentGroupBy
            const team = vm.currentTeam
            if (!selection) return
            if ((selection?.weekStatus?.value ?? selection?.weekStatus) !== (weekOption?.value ?? weekOption)) {
                if (groupBy === 'students')
                    await vm.updateUserWeekStatus(selection.objectID, 'weekStatusOption', weekOption)
                if (groupBy === 'groups') {
                    await db.collection('groups').doc(selection.id).update({ weekStatusOption: weekOption })
                    await Promise.all((selection?.students || []).map(studentId =>
                        vm.updateUserWeekStatus(studentId, 'weekStatusOption', weekOption)
                    ))
                }
                if (groupBy === 'teams') {
                    let groups = [...team?.groups || []]
                    let findGroupIndex = groups.findIndex(group => group.id === selection.id)
                    if (findGroupIndex >= 0) groups[findGroupIndex].weekStatusOption = weekOption
                    await db.collection('teams').doc(selection.teamId).update({ groups: groups })

                    if (findGroupIndex >= 0)
                        await Promise.all((groups[findGroupIndex]?.students || []).map(studentId =>
                            vm.updateUserWeekStatus(studentId, 'weekStatusOption', weekOption)
                        ))
                }
            }
        },
        async updateUserWeekStatus(studentId, attribute, value) {
            const vm = this
            await db.collection('users').doc(studentId).update({ [attribute]: value })
                .catch(err => { })
            if (attribute === 'weekStatus') {
                const findUserIndex = vm.students.findIndex(student => student.objectID === studentId)
                if (findUserIndex >= 0) vm.students[findUserIndex].rightIcon = vm.getIconWeek(value)
            }
        },
        onSelectMonthCalendar(date) {
            const vm = this;
            const selected = vm.$moment(date, 'YYYY-MM-DD')
            const current = vm.$moment(vm.currentCalendarDate, 'YYYY-MM-DD')
            const diffMonths = selected.diff(current, 'months')
            vm.$refs.calendar.move(diffMonths)
        },
    },
    watch: {
        async coachId() {
            const vm = this
            vm.currentStudent = null
            await vm.loadStudents()
        },
        currentStudent(val) {
            this.triggerUpdateCalendar(val)
        },
        currentGroup(val) {
            this.triggerUpdateCalendar(val)
        },
        currentTeam(val) {
            this.triggerUpdateCalendar(val)
        },
        currentTeamGroup(val) {
            this.triggerUpdateCalendar(val)
        },
        currentGroupBy(val) {
            if (val === 'students') {
                this.currentGroup = null
                this.currentTeam = null
                this.currentTeamGroup = null
            }
            if (val === 'groups') {
                this.currentStudent = null
                this.currentTeam = null
                this.currentTeamGroup = null
            }
            if (val === 'teams') {
                this.currentStudent = null
                this.currentGroup = null
            }
        },
        async currentWeekStatus(val, oldVal) {
            if (val === oldVal) return
            if (!oldVal && !this.currentSelection) return
            const vm = this
            const selection = vm.currentSelection
            const groupBy = vm.currentGroupBy
            const team = vm.currentTeam
            if (!selection) return
            if (groupBy === 'students')
                await vm.updateUserWeekStatus(selection.objectID, 'weekStatus', val)
            if (groupBy === 'groups') {
                await db.collection('groups').doc(selection.id).update({ weekStatus: val })
                await Promise.all((selection?.students || []).map(studentId =>
                    vm.updateUserWeekStatus(studentId, 'weekStatus', val)
                ))
            }
            if (groupBy === 'teams') {
                let groups = [...team?.groups || []]
                let findGroupIndex = groups.findIndex(group => group.id === selection.id)
                if (findGroupIndex >= 0) groups[findGroupIndex].weekStatus = val
                await db.collection('teams').doc(selection.teamId).update({ groups: groups })

                if (findGroupIndex >= 0)
                    await Promise.all((groups[findGroupIndex]?.students || []).map(studentId =>
                        vm.updateUserWeekStatus(studentId, 'weekStatus', val)
                    ))
            }
        },
        highestBlock(val) {
            const vm = this
            if (val > vm.blockColors.length) {
                for (let i = vm.blockColors.length; i < val; i++) vm.blockColors.push(vm.blockColorsBase[i % vm.blockColorsBase.length])
            } else if (val < vm.blockColors.length) vm.blockColors = vm.blockColors.slice(0, val)
        },
        dateType() {
            if (this.isDayView) this.libraryOpen = false
            this.$nextTick(this.updateWeekHeight)
        },
        progressView(val, oldVal) {
            if (!val && oldVal) this.refreshCalendarEvents()
        },
        libraryOpen(val) {
            const vm = this
            if (val) {
                vm.$nextTick(vm.updateLibraryTop)
                window.addEventListener('scroll', vm.updateLibraryTop, true)
                window.addEventListener('resize', vm.updateLibraryTop)
            } else {
                window.removeEventListener('scroll', vm.updateLibraryTop, true)
                window.removeEventListener('resize', vm.updateLibraryTop)
            }
        },
        loadingEvents(val) {
            if (!val) this.$nextTick(this.updateWeekHeight)
        },
        currentRoutines(val) {
            let highestBlock = 0
            val.forEach(routine => {
                if (routine.blocks?.length > highestBlock) highestBlock = routine.blocks.length
            })
            this.highestBlock = highestBlock
        },
    }
}
</script>

<style scoped>
/* ── HEADER ── */
.cal-header {
    margin: -12px -12px 0;
}

/* ── BODY ── */
.cal-body {
    margin: 0 -12px -12px;
    padding-bottom: 72px;
}

.cal-body.is-day {
    padding-bottom: 0;
}

.cal-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 64px 24px;
    text-align: center;
}

.cal-empty-text {
    font-size: 14px;
    color: var(--v-txtMuted-base);
    max-width: 360px;
}

.cal-loader {
    margin: 0 -12px;
}

.tabs-row {
    min-height: 48px;
    border-top: 1px solid var(--v-outline-base);
    border-bottom: 1px solid var(--v-outline-base);
    padding: 8px 20px 0;
    display: flex;
    align-items: center;
}

.filters-row {
    min-height: 52px;
    border-bottom: 1px solid var(--v-outline2-base);
    padding: 8px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
}

.filters-left {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.filters-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cal-history-sep {
    width: 1px;
    height: 22px;
    background: var(--v-outline-base);
}

.view-btn {
    height: 34px;
    padding: 0 16px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    white-space: nowrap;
    transition: border-color .15s, background .15s, color .15s;
}

.view-btn:hover {
    border-color: var(--v-outline2-base);
}

.view-btn.active {
    background: rgba(204, 255, 51, 0.12);
    color: var(--v-primary-base);
    font-weight: 600;
    border-color: rgba(204, 255, 51, 0.3);
}

.theme--light .view-btn.active {
    background: rgba(102, 204, 204, 0.12);
    color: var(--v-secondary-base);
    border-color: rgba(102, 204, 204, 0.3);
}

.worktype-row {
    min-height: 44px;
    padding: 6px 20px;
    background: var(--v-bg-base);
    border-bottom: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
}

.worktype-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.worktype-right {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.view-btn--icon {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.view-btn--icon .v-icon {
    color: inherit !important;
}

.view-btn.view-btn--on {
    background: rgba(51, 153, 153, .12);
    border-color: rgba(51, 153, 153, .3);
    color: var(--v-accent-base);
    font-weight: 600;
}

.cal-body {
    transition: margin-right .22s cubic-bezier(.4, 0, .2, 1);
}

.cal-body.with-library {
    margin-right: 328px;
}

.calendar ::v-deep .cal-lib-target {
    position: relative;
    box-shadow: inset 0 0 0 2px var(--v-accent-base);
    background: rgba(51, 153, 153, .06);
}

.theme--dark .calendar ::v-deep .cal-lib-target {
    box-shadow: inset 0 0 0 2px var(--v-primary-base);
    background: rgba(204, 255, 51, .05);
}

.calendar ::v-deep .cal-lib-target[data-ghost]::after {
    content: attr(data-ghost);
    position: absolute;
    left: 6px;
    right: 6px;
    bottom: 6px;
    z-index: 3;
    padding: 5px 8px;
    border-radius: 8px;
    border: 1px dashed var(--v-accent-base);
    background: var(--v-surface-base);
    color: var(--v-accent-base);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .04em;
    text-align: center;
    text-transform: uppercase;
}

.theme--dark .calendar ::v-deep .cal-lib-target[data-ghost]::after {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
}

.lib-ghost {
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--v-txtPrimary-base);
}

.worktype-label {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}

.header-select {
    max-width: 220px;
}

.worktype-select {
    max-width: 140px;
}

>>>.v-calendar-daily__scroll-area {
    display: none;
}

>>>.v-calendar-daily__intervals-head {
    display: none;
}

.calendar.is-week >>> .v-calendar-daily__body {
    display: none;
}

.calendar.is-week >>> .v-calendar-daily__head {
    flex: 1 1 auto;
    min-height: var(--week-head-h, calc(100vh - 250px));
}

.calendar.is-week >>> .v-calendar-daily_head-day {
    display: flex;
    flex-direction: column;
    background: var(--v-bg-base);
    border: none !important;
    box-shadow: inset -1px 0 0 0 var(--v-outline2-base);
}

.calendar.is-week >>> .v-calendar-daily_head-day:last-child {
    box-shadow: none;
}

.calendar.is-week >>> .v-calendar-daily_head-weekday {
    text-transform: uppercase;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: .06em;
    color: var(--v-txtMuted-base);
    text-align: center;
    padding: 10px 8px 0;
    background: var(--v-surface-base);
}

.calendar.is-week >>> .v-calendar-daily_head-day-label {
    padding: 0;
    background: var(--v-surface-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.calendar {
    border-radius: 0 !important;
    margin: 0 !important;
    overflow: hidden;
    background: var(--v-bg-base) !important;
    border: none !important;
    border-bottom: 1px solid var(--v-outline-base) !important;
}

/* Day labels (weekday headers): surface bg, bottom border only (no vertical dividers) */
>>>.v-calendar-weekly__head-weekday,
>>>.v-calendar-weekly__head-weekday.v-outside {
    text-transform: uppercase !important;
    font-weight: 600;
    font-size: 11px;
    line-height: 13px;
    letter-spacing: .06em;
    color: var(--v-txtMuted-base) !important;
    padding-top: 10px;
    padding-bottom: 8px;
    background: var(--v-surface-base) !important;
    border-right: none !important;
    border-bottom: 1px solid var(--v-outline-base) !important;
}

/* Day cells: bg background, horizontal divider via shadow, vertical via overlay */
>>>.v-calendar-weekly__day,
>>>.v-calendar-weekly__day.v-outside {
    background: var(--v-bg-base) !important;
    border: none !important;
    box-shadow: inset 0 -1px 0 0 var(--v-outline2-base);
    position: relative;
}

>>>.v-calendar-weekly__day::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 1px;
    background: var(--v-outline2-base);
    pointer-events: none;
    z-index: 1;
}

::v-deep .v-calendar .v-event {
    background-color: transparent !important;
    overflow: visible !important;
    width: 100% !important;
}

::v-deep .v-calendar .v-event > div {
    height: 100%;
}

.event-block-start,
.event-has-handle {
    position: relative;
}

.block-handle {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 6px;
    height: 11px;
    padding: 0 6px;
    overflow: hidden;
    border-radius: 5px;
    cursor: grab;
    user-select: none;
    transition: height .12s, top .12s, background .12s, opacity .12s;
}

.block-handle:hover {
    top: -5px;
    height: 24px;
    background: var(--v-surface2-base);
    box-shadow: inset 0 0 0 1px var(--v-outline2-base);
}

.cal-body.is-dragging .block-handle {
    opacity: 0;
    pointer-events: none;
    top: 0;
    height: 11px;
    background: transparent;
    box-shadow: none;
}

.block-handle .block-handle-icon,
.block-handle .block-handle-accent,
.block-handle .block-handle-label {
    opacity: 0;
    transition: opacity .08s;
}

.block-handle:hover .block-handle-icon,
.block-handle:hover .block-handle-accent,
.block-handle:hover .block-handle-label {
    opacity: 1;
}

.block-handle-icon {
    color: var(--v-txtMuted-base) !important;
    flex-shrink: 0;
}

.block-handle-accent {
    width: 2px;
    height: 13px;
    border-radius: 1px;
    flex-shrink: 0;
    background: var(--block-accent, var(--v-accent-base));
}

.block-handle-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}

.event-block-start::before {
    content: '';
    position: absolute;
    top: 0;
    left: 2px;
    right: 2px;
    height: 1px;
    background: var(--v-outline2-base);
    z-index: 2;
}

::v-deep .v-calendar-weekly__day {
    display: flex;
    flex-direction: column;
}

.day-slot-content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
}

.day-slot-content > .calendar-free-container {
    flex: 1 1 auto;
}

.drag-ghost {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99999;
    width: auto;
    max-width: 220px;
    opacity: 0.95;
    pointer-events: none;
    background: var(--v-surface-base);
    border: 1px solid var(--v-accent-base);
    border-radius: 6px;
    padding: 4px 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    will-change: transform;
}

.drag-source {
    opacity: 0.4;
    outline: 1px dashed var(--v-accent-base);
    outline-offset: -1px;
    border-radius: 4px;
}

.drag-source-block {
    outline: none;
    background: rgba(102, 204, 204, 0.06);
    border-radius: 0;
}

.drop-target-ok,
.drop-target-block {
    position: relative;
}

.drop-target-ok::after,
.drop-target-block::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 1px;
    height: var(--drop-h, 100%);
    background-color: rgba(102, 204, 204, 0.1);
    box-shadow: inset 0 0 0 1.5px var(--v-accent-base);
    border-radius: 4px;
    pointer-events: none;
    z-index: 3;
}

.drop-target-invalid {
    background-color: rgba(232, 92, 92, 0.12);
    box-shadow: inset 0 0 0 1.5px var(--v-error-base);
    border-radius: 4px;
    cursor: not-allowed;
}

>>>.v-calendar-weekly__day.day-drop-invalid::before {
    content: '';
    position: absolute;
    top: var(--label-h, 34px);
    left: 0;
    right: 0;
    bottom: 0;
    box-shadow: inset 0 0 0 1.5px var(--v-error-base);
    background-color: rgba(232, 92, 92, 0.06);
    border-radius: 6px;
    pointer-events: none;
    z-index: 3;
}

.calendar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.switch-routine-wrapper {
    position: fixed;
    right: 16px;
    bottom: 20px;
    z-index: 6;
}

.switch-routine-fab {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline2-base);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s, transform 0.2s;
}

.switch-routine-fab:hover {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
    transform: scale(1.05);
}

.switch-routine-fab .v-icon {
    color: inherit !important;
}

.switch-routine-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--v-primary-base);
    border: 1.5px solid var(--v-bg-base);
    color: #000;
    font-size: 9px;
    font-weight: 800;
}
</style>
